/* global globalThis */
import { jsPDF } from "jspdf";
import { formatDateTime, formatTimeOnly } from "./formatDateTime";
import {
  fetchDraftPayloadApi,
  fetchAssessmentGroupById,
  fetchAssessmentGroups,
  fetchAssessmentTemplateByType,
  parseSchemaPayload,
  getTemplateSchemaFromPayload,
  cleanProgramId,
  isEnrolledOutcome,
  isDeclinedOutcome,
  isProgramCompletionOutcome,
  fetchAssessmentReferenceData,
  getCachedAssessmentReferenceData,
} from "../api/assessment";
import { fetchMemberOverview } from "../api/priority";

/**
 * Base64-encoded PNG of the LucyRx brand logo (derived from src/assets/lucy.svg).
 * Allows crisp vector-equivalent rendering across all environments without network dependency.
 */
const LUCY_LOGO_BASE64 =
  "data:image/png;base64,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";

/**
 * Formats a program identifier or code into clean title case.
 */
export const formatProgramName = (rawProgram) => {
  if (!rawProgram) return "General";
  return String(rawProgram)
    .replaceAll(/_PROGRAM_SPECIFIC|_PROGRAM/gi, "")
    .replaceAll(/\bprogram\b/gi, "")
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
    .trim();
};

/**
 * Converts a string so that every word's first letter is capitalized, and remaining letters lowercase.
 */
export const normalizeOutreachLabel = (val) => {
  if (val === undefined || val === null) return "";
  if (typeof val !== "string") return String(val);
  return val
    .replaceAll(/\bFirst\s+outreach\b/g, "1st outreach")
    .replaceAll(/\bSecond\s+outreach\b/g, "2nd outreach")
    .replaceAll(/\bFirst\s+Outreach\b/g, "1st Outreach")
    .replaceAll(/\bSecond\s+Outreach\b/g, "2nd Outreach")
    .replaceAll(/\bfirst\s+outreach\b/g, "1st outreach")
    .replaceAll(/\bsecond\s+outreach\b/g, "2nd outreach")
    .replaceAll(/\bFIRST[\s_]+OUTREACH\b/g, "1st Outreach")
    .replaceAll(/\bSECOND[\s_]+OUTREACH\b/g, "2nd Outreach");
};

/**
 * Converts a string so that every word's first letter is capitalized, and remaining letters lowercase.
 */
export const toTitleCaseWords = (str) => {
  if (!str || typeof str !== "string") return "";
  const res = str
    .toLowerCase()
    .replaceAll(/(?:^|[\s_/-])\w/g, (char) => char.toUpperCase());
  return normalizeOutreachLabel(res);
};

/**
 * Formats a program outcome or reason string: removes underscore (_) characters
 * and capitalizes the first letter of each word (Title Case).
 */
export const formatOutcome = (outcome) => {
  if (
    !outcome ||
    String(outcome).trim() === "" ||
    String(outcome).toUpperCase() === "N/A" ||
    String(outcome).trim() === "—"
  ) {
    return "N/A";
  }
  let str = String(outcome)
    .replaceAll("_", " ")
    .trim()
    .toLowerCase()
    .replaceAll(/(?:^|[-/({[\s_])\w/g, (char) => char.toUpperCase());

  if (/\bFirst\s+outreach\b/i.test(outcome)) {
    const hasLowerOutreach =
      /\bFirst\s+outreach\b/.test(outcome) ||
      /\bfirst\s+outreach\b/.test(outcome);
    str = str.replaceAll(
      /\bFirst\s+Outreach\b/gi,
      hasLowerOutreach ? "1st outreach" : "1st Outreach",
    );
  } else {
    str = str.replaceAll(/\bFirst\s+Outreach\b/gi, "1st Outreach");
  }

  if (/\bSecond\s+outreach\b/i.test(outcome)) {
    const hasLowerOutreach =
      /\bSecond\s+outreach\b/.test(outcome) ||
      /\bsecond\s+outreach\b/.test(outcome);
    str = str.replaceAll(
      /\bSecond\s+Outreach\b/gi,
      hasLowerOutreach ? "2nd outreach" : "2nd Outreach",
    );
  } else {
    str = str.replaceAll(/\bSecond\s+Outreach\b/gi, "2nd Outreach");
  }

  return str;
};

/**
 * Formats an assessment type code into clean human-readable text.
 */
export const formatAssessmentTypeLabel = (rawType) => {
  if (
    !rawType ||
    String(rawType).trim() === "" ||
    String(rawType).toUpperCase() === "N/A" ||
    String(rawType).toUpperCase() === "NA"
  ) {
    return "";
  }
  const norm = String(rawType).toUpperCase().trim();
  const labelMap = {
    INITIAL: "Initial Assessment",
    FOLLOW_UP: "Follow-up Assessment",
    FOLLOWUP: "Follow-up Assessment",
    "FOLLOW-UP": "Follow-up Assessment",
    DISCHARGE: "Discharge Assessment",
    FIRST_OUTREACH: "1st Outreach",
    SECOND_OUTREACH: "2nd Outreach",
  };
  if (labelMap[norm]) {
    return labelMap[norm];
  }
  let res = norm
    .replaceAll("_", " ")
    .toLowerCase()
    .replaceAll(/(?:^|\s|[-/])\w/g, (char) => char.toUpperCase());
  return normalizeOutreachLabel(res);
};

/**
 * Formats a date string to MM/DD/YYYY using UTC to avoid timezone day shifts.
 */
export const formatDobOrDate = (dateValue) => {
  if (!dateValue) return "N/A";
  try {
    const s = String(dateValue).trim();
    if (!s) return "N/A";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
    const d = new Date(
      s.includes("T") || s.endsWith("Z") ? s : `${s}T00:00:00Z`,
    );
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch (e) {
    console.error("Error formatting date:", e);
    return String(dateValue);
  }
};

/**
 * Formats a string to Title Case where each word begins with a capital letter followed by lowercase letters.
 */
export const toTitleCase = (str) => {
  if (!str) return "";
  const res = String(str)
    .toLowerCase()
    .replaceAll(/(?:^|\s|[-_/(])\w/g, (char) => char.toUpperCase());
  return normalizeOutreachLabel(res);
};

/**
 * Resolves the effective label for a field or section, preserving suffixes (e.g. test_2)
 * when a generic label like "test" is present.
 */
export const getEffectiveLabel = (fieldOrLabel, key) => {
  if (!fieldOrLabel && !key) return "";
  const label =
    typeof fieldOrLabel === "string"
      ? fieldOrLabel
      : fieldOrLabel?.label || fieldOrLabel?.title || fieldOrLabel?.name || "";
  const objKey =
    typeof fieldOrLabel === "object"
      ? String(
          fieldOrLabel?.key || fieldOrLabel?.id || fieldOrLabel?.name || "",
        ).trim()
      : "";
  const explicitKey = key || objKey;
  const keyStr = String(explicitKey || "").trim();
  const labelStr = String(label || "").trim();

  let leafKey = keyStr.split(".").pop();
  if (objKey && /_\d+$/.test(objKey)) {
    leafKey = objKey.split(".").pop();
  }

  const normLeaf = leafKey
    .toLowerCase()
    .replace(/_\d+$/, "")
    .replaceAll(/[^a-z0-9]/g, "");
  const normLabel = labelStr.toLowerCase().replaceAll(/[^a-z0-9]/g, "");

  if (
    /_\d+$/.test(leafKey) &&
    labelStr &&
    !/_\d+$/.test(labelStr) &&
    (!normLabel ||
      !normLeaf ||
      normLabel === normLeaf ||
      normLabel.includes(normLeaf) ||
      normLeaf.includes(normLabel))
  ) {
    return leafKey;
  }

  if (
    (leafKey.includes("_") || /\d$/.test(leafKey)) &&
    labelStr &&
    !labelStr.includes("_") &&
    !/\d$/.test(labelStr) &&
    leafKey.toLowerCase().startsWith(labelStr.toLowerCase())
  ) {
    return leafKey;
  }
  return labelStr || leafKey || keyStr;
};

/**
 * Resolves the effective label for an option, preserving suffixes like test_2.
 */
export const getEffectiveOptionLabel = (opt) => {
  if (!opt) return "";
  if (typeof opt === "string") return opt;
  const optVal = String(
    opt?.value ?? opt?.key ?? opt?.id ?? opt?.name ?? "",
  ).trim();
  const optLabel = String(opt?.label ?? "").trim();
  const leafVal = optVal.split(".").pop();
  const normLeaf = leafVal
    .toLowerCase()
    .replace(/_\d+$/, "")
    .replaceAll(/[^a-z0-9]/g, "");
  const normLabel = optLabel.toLowerCase().replaceAll(/[^a-z0-9]/g, "");

  if (
    /_\d+$/.test(leafVal) &&
    optLabel &&
    !/_\d+$/.test(optLabel) &&
    (!normLabel ||
      !normLeaf ||
      normLabel === normLeaf ||
      normLabel.includes(normLeaf) ||
      normLeaf.includes(normLabel))
  ) {
    return leafVal;
  }

  if (
    (leafVal.includes("_") || /\d$/.test(leafVal)) &&
    optLabel &&
    !optLabel.includes("_") &&
    !/\d$/.test(optLabel) &&
    leafVal.toLowerCase().startsWith(optLabel.toLowerCase())
  ) {
    return optVal;
  }
  return optLabel || optVal || "";
};

/**
 * Formats a key or label, preserving suffix keys (e.g. test_2) while formatting normal keys to Title Case.
 */
export const formatKeyOrLabel = (key, label) => {
  const eff = getEffectiveLabel(label, key);
  const keyStr = String(key || "").trim();
  const leafKey = keyStr.split(".").pop();
  const isGenericPlaceholder = /^(sub_field|field|group_field)_\d+$/i.test(
    leafKey,
  );

  if (
    !isGenericPlaceholder &&
    ((eff && /_\d+$/.test(eff)) || (leafKey && /_\d+$/.test(leafKey)))
  ) {
    return eff && /_\d+$/.test(eff) ? eff : leafKey;
  }

  if (label && String(label).trim()) {
    const trimmedLabel = String(label).trim();
    if (!isGenericPlaceholder) {
      return trimmedLabel;
    }
  }

  if (eff && eff !== leafKey && eff !== keyStr && !isGenericPlaceholder) {
    return eff;
  }

  if (
    leafKey &&
    !leafKey.includes("_") &&
    !leafKey.includes("-") &&
    !isGenericPlaceholder
  ) {
    return leafKey;
  }

  const cleanStr = String(leafKey || keyStr || eff).replaceAll(/[_-]+/g, " ");
  return toTitleCase(cleanStr);
};

/**
 * Formats a subsection header title with its first letter in Capital (e.g. Test, Test_2, Jh).
 */
export const formatSectionHeaderTitle = (title, key) => {
  let raw = String(title || key || "").trim();
  if (!raw) return "Section";
  if (/_\d+$/.test(raw)) {
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
  if (raw.includes(" ")) {
    return raw
      .split(" ")
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
      .join(" ");
  }
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

/**
 * Extracts human-readable section label from a schema section, matching the Clinical Assessment UI.
 */
export const getSectionLabel = (section, sectionKey, fallbackTitle = "") => {
  const sKey = sectionKey || section?.key || section?.id || section?.name || "";
  if (sKey && /_\d+$/.test(sKey)) {
    return formatSectionHeaderTitle(sKey);
  }
  let label = getEffectiveLabel(section, sKey);
  if (!label) {
    if (section?.title && String(section.title).trim()) {
      label = String(section.title).trim();
    } else if (section?.name && String(section.name).trim()) {
      label = String(section.name).trim();
    } else if (sKey) {
      label = sKey;
    }
  }
  if (!label) {
    label = fallbackTitle || "Assessment Section";
  }
  return formatSectionHeaderTitle(label, sKey);
};

/**
 * Resolves the formatted nurse/clinician signee name with proper credentials (e.g. RN).
 */
export const getEffectiveSigneeName = (
  explicitSignee,
  submittedBy,
  assigneeRnName,
) => {
  const candidate = assigneeRnName || explicitSignee || submittedBy;
  if (
    candidate &&
    String(candidate).trim() &&
    candidate !== "N/A" &&
    candidate !== "—"
  ) {
    const trimmed = String(candidate).trim();
    if (/\b(RN|NP|MD|DO|PA|LPN)\b/i.test(trimmed)) {
      return trimmed;
    }
    return `${trimmed}, RN`;
  }

  return "";
};

/**
 * Resolves formatted signature timestamp (MM/DD/YYYY hh:mm A).
 */
export const getEffectiveSignatureDate = (
  explicitDate,
  completionTime,
  startTime,
) => {
  const d = explicitDate || completionTime || startTime;
  if (!d) {
    const now = new Date();
    return `${now.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })} ${formatTimeOnly(now)}`;
  }
  return formatDateTime(d);
};

const unwrapObjectValue = (val) => {
  if (val.branches && typeof val.branches === "object") return val;
  if ("value" in val && val.value !== undefined) {
    const eff = getEffectiveOptionLabel(val);
    return eff && eff !== val.label ? eff : val.value;
  }
  if (val.is_checked !== undefined) return val.is_checked;
  if (val.selected !== undefined) return val.selected;
  if (val.answer !== undefined) return val.answer;
  if (val.label !== undefined && !val.fields) return val.label;
  return val;
};

const unwrapValue = (val) => {
  if (!val || typeof val !== "object" || Array.isArray(val)) return val;
  return unwrapObjectValue(val);
};

const isBooleanAnswered = (val) => {
  const unwrapped = unwrapValue(val);
  const str = String(unwrapped).toLowerCase();
  return (
    unwrapped === true ||
    unwrapped === false ||
    str === "true" ||
    str === "false" ||
    str === "yes" ||
    str === "no"
  );
};

const isCheckboxField = (field) => {
  if (!field || typeof field !== "object") return false;
  return (
    field.type === "checkbox" ||
    Boolean(field.checkboxLabel) ||
    field.widget === "checkbox" ||
    field.inputType === "checkbox"
  );
};

const isCheckboxAnswered = (val) => {
  const unwrapped = unwrapValue(val);
  const str = String(unwrapped).toLowerCase();
  return unwrapped === true || str === "true" || str === "yes";
};

const isMultiSelectAnswered = (val) => {
  const unwrapped = unwrapValue(val);
  if (Array.isArray(unwrapped)) {
    return unwrapped.some((item) => {
      if (item && typeof item === "object") {
        const itemVal = item.label || item.value || item.name;
        return itemVal != null && String(itemVal).trim() !== "";
      }
      return item != null && String(item).trim() !== "";
    });
  }
  return String(unwrapped).trim() !== "";
};

const isGroupAnswered = (field, actualVal, rawVal) => {
  if (field?.fields && typeof field.fields === "object") {
    return Object.keys(field.fields).some((subKey) => {
      const subField = field.fields[subKey];
      const subVal =
        actualVal?.[subKey] ??
        (typeof rawVal === "object" ? rawVal?.[subKey] : undefined);
      return isValueAnswered(subField, subVal);
    });
  }
  return false;
};

/**
 * Checks if a question value is considered "answered".
 */
export const isValueAnswered = (field, val) => {
  if (val === undefined || val === null) return false;

  const actualVal = unwrapValue(val);

  if (actualVal === undefined || actualVal === null) return false;

  if (
    actualVal &&
    typeof actualVal === "object" &&
    actualVal.branches &&
    actualVal.value !== undefined
  ) {
    return isValueAnswered(field, actualVal.value);
  }

  if (typeof actualVal === "object" && !Array.isArray(actualVal)) {
    const keys = Object.keys(actualVal);
    if (keys.length === 0) return false;
    return keys.some((k) => isValueAnswered(null, actualVal[k]));
  }

  if (
    !field &&
    (actualVal === false || String(actualVal).toLowerCase() === "false")
  ) {
    return false;
  }

  if (isCheckboxField(field)) return isCheckboxAnswered(actualVal);
  const fieldType = field?.type || "text";
  if (fieldType === "boolean") return isBooleanAnswered(actualVal);
  if (fieldType === "checkbox") return isCheckboxAnswered(actualVal);
  if (fieldType === "multi_select") return isMultiSelectAnswered(actualVal);
  if (fieldType === "group") return isGroupAnswered(field, actualVal, val);

  const strVal = String(actualVal).trim();
  return !(
    !strVal ||
    strVal === "undefined" ||
    strVal === "null" ||
    strVal === "[object Object]"
  );
};

const formatBooleanAnswer = (actualVal) => {
  const unwrapped = unwrapValue(actualVal);
  const s = String(unwrapped).toLowerCase();
  if (unwrapped === true || s === "true" || s === "yes") return "Yes";
  if (unwrapped === false || s === "false" || s === "no") return "No";
  return "";
};

const getSingleSelectTarget = (actualVal) => {
  if (!actualVal || typeof actualVal !== "object") return actualVal;
  const val = actualVal.value === undefined ? actualVal.label : actualVal.value;
  return val || actualVal.name || actualVal;
};

const formatSingleSelectAnswer = (field, actualVal) => {
  const target = getSingleSelectTarget(actualVal);
  if (field?.options && Array.isArray(field.options)) {
    const opt = field.options.find(
      (o) =>
        o.value === target ||
        o.label === target ||
        String(o.value) === String(target) ||
        String(o.label) === String(target),
    );
    if (opt) {
      return getEffectiveOptionLabel(opt) || opt.label || String(target);
    }
  }
  return String(target);
};

const formatObjectAnswer = (actualVal, field) => {
  if (actualVal.branches && actualVal.value !== undefined) {
    return formatAnswer(field, actualVal.value);
  }
  if (
    actualVal.label &&
    (actualVal.value !== undefined ||
      actualVal.key !== undefined ||
      actualVal.id !== undefined ||
      actualVal.name !== undefined)
  ) {
    const eff = getEffectiveOptionLabel(actualVal);
    if (eff) return normalizeOutreachLabel(eff);
  }
  const entries = Object.entries(actualVal).filter(
    ([k, v]) => !k.startsWith("_") && v !== undefined && v !== null && v !== "",
  );
  if (entries.length > 0) {
    return normalizeOutreachLabel(
      entries
        .map(([k, v]) => {
          const label = formatKeyOrLabel(k);
          const valStr =
            typeof v === "object" ? formatAnswer(null, v) : String(v).trim();
          return `${label}: ${valStr}`;
        })
        .join(", "),
    );
  }
  return "";
};

const formatMultiSelectAnswer = (field, actualVal) => {
  if (Array.isArray(actualVal)) {
    return normalizeOutreachLabel(
      actualVal
        .map((item) => {
          if (item && typeof item === "object") {
            const eff = getEffectiveOptionLabel(item);
            if (eff) return eff;
            return item.label || item.value || item.name || "";
          }
          if (field?.options && Array.isArray(field.options)) {
            const matchedOpt = field.options.find(
              (o) =>
                o.value === item ||
                o.label === item ||
                String(o.value) === String(item) ||
                String(o.label) === String(item),
            );
            if (matchedOpt) {
              return getEffectiveOptionLabel(matchedOpt);
            }
          }
          return item;
        })
        .filter((item) => item != null && String(item).trim() !== "")
        .join(", "),
    );
  }
  return normalizeOutreachLabel(String(actualVal));
};

/**
 * Formats an answered value for human-readable clinical report display.
 */
export const formatAnswer = (field, val) => {
  if (val === undefined || val === null) return "";

  const actualVal = unwrapValue(val);

  if (typeof actualVal === "object" && !Array.isArray(actualVal)) {
    return formatObjectAnswer(actualVal, field);
  }

  if (isCheckboxField(field)) return actualVal ? "Yes" : "";

  const fieldType = field?.type || "text";

  if (fieldType === "boolean")
    return normalizeOutreachLabel(formatBooleanAnswer(actualVal));
  if (fieldType === "checkbox") return actualVal ? "Yes" : "";
  if (fieldType === "multi_select")
    return formatMultiSelectAnswer(field, actualVal);
  if (fieldType === "single_select") {
    return normalizeOutreachLabel(formatSingleSelectAnswer(field, actualVal));
  }
  if (fieldType === "date") return formatDobOrDate(actualVal);

  return normalizeOutreachLabel(String(actualVal).trim());
};

const isSpecialClinicalNoteField = (fieldKey, sectionKey) => {
  const normFieldKey = String(fieldKey || "").toLowerCase();
  const normSecKey = String(sectionKey || "").toLowerCase();
  return (
    normSecKey.includes("narrative_attestation") ||
    normSecKey.includes("outcome_and_sign") ||
    normFieldKey === "clinician_signature" ||
    normFieldKey === "signature_date" ||
    normFieldKey === "narrative_note" ||
    normFieldKey === "clinical_note" ||
    normFieldKey === "clinical_notes" ||
    normFieldKey === "narrative"
  );
};

const normalizeBooleanComparisonValue = (val) => {
  if (val === true || val === "true" || val === "yes" || val === "Yes")
    return true;
  if (val === false || val === "false" || val === "no" || val === "No")
    return false;
  return val;
};

const findValInContainer = (container, candidateKeys) => {
  if (!container || typeof container !== "object") return undefined;
  for (const k of candidateKeys) {
    if (!k) continue;
    if (
      container[k] !== undefined &&
      container[k] !== null &&
      container[k] !== ""
    ) {
      return container[k];
    }
  }

  const containerKeys = Object.keys(container);
  for (const k of candidateKeys) {
    if (!k) continue;
    const lowerK = String(k).trim().toLowerCase();
    const matched = containerKeys.find(
      (ck) => ck.trim().toLowerCase() === lowerK,
    );
    if (
      matched &&
      container[matched] !== undefined &&
      container[matched] !== null &&
      container[matched] !== ""
    ) {
      return container[matched];
    }
  }
  return undefined;
};

const getFieldCandidateKeys = (field, fieldKey) => {
  const keys = new Set();
  if (fieldKey) {
    keys.add(fieldKey);
    keys.add(fieldKey.toLowerCase());
    keys.add(fieldKey.replaceAll(/[^a-zA-Z0-9]/g, "_").toLowerCase());
  }
  if (field?.key) {
    keys.add(field.key);
    keys.add(field.key.toLowerCase());
  }
  if (field?.id) {
    keys.add(field.id);
    keys.add(field.id.toLowerCase());
  }
  if (field?.name) {
    keys.add(field.name);
    keys.add(field.name.toLowerCase());
  }
  if (field?.label) {
    keys.add(field.label);
    keys.add(field.label.toLowerCase());
    const snake = String(field.label)
      .trim()
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, "_");
    if (snake) keys.add(snake);
  }
  return Array.from(keys);
};

/**
 * Trims leading and trailing underscores from a string without regular expressions.
 */
const trimUnderscores = (val) => {
  if (!val) return "";
  let start = 0;
  let end = val.length;
  while (start < end && val[start] === "_") {
    start += 1;
  }
  while (end > start && val[end - 1] === "_") {
    end -= 1;
  }
  return val.slice(start, end);
};

/**
 * Converts a string to camelCase without regular expressions that cause backtracking.
 */
const toCamelCase = (str) => {
  if (!str) return "";
  let result = "";
  let capitalizeNext = false;
  for (let i = 0; i < str.length; i += 1) {
    const code = str.codePointAt(i);
    const isAlphaNum =
      (code >= 48 && code <= 57) || // 0-9
      (code >= 65 && code <= 90) || // A-Z
      (code >= 97 && code <= 122); // a-z

    if (isAlphaNum) {
      const char = str[i];
      if (result.length === 0) {
        result += char.toLowerCase();
      } else if (capitalizeNext) {
        result += char.toUpperCase();
      } else {
        result += char;
      }
      capitalizeNext = false;
    } else if (result.length > 0) {
      capitalizeNext = true;
    }
  }
  return result;
};

const generateCandidateSectionKeys = (
  sectionKey,
  schemaSection,
  requiredSuffix,
) => {
  const candidateSecKeys = new Set();

  const addVariations = (keyStr) => {
    if (!keyStr) return;
    const str = String(keyStr).trim();
    if (requiredSuffix) {
      if (!str.endsWith(requiredSuffix)) {
        return;
      }
    } else if (/_(\d+)$/.test(str)) {
      return;
    }
    candidateSecKeys.add(str);
    candidateSecKeys.add(str.toLowerCase());
    candidateSecKeys.add(str.toUpperCase());
    const noPrefix = str.replace(/^\d+[_-]/, "");
    if (noPrefix !== str) {
      candidateSecKeys.add(noPrefix);
      candidateSecKeys.add(noPrefix.toLowerCase());
    }
    const snake = trimUnderscores(
      str.replaceAll(/[^a-zA-Z0-9]+/g, "_").toLowerCase(),
    );
    if (snake) {
      candidateSecKeys.add(snake);
      const snakeNoPrefix = snake.replace(/^\d+_/, "");
      candidateSecKeys.add(snakeNoPrefix);
    }
    const camel = toCamelCase(str);
    if (camel) candidateSecKeys.add(camel);
    const clean = str.replaceAll(/[^a-zA-Z0-9]/g, "").toLowerCase();
    if (clean) candidateSecKeys.add(clean);
  };

  if (sectionKey) addVariations(sectionKey);
  if (schemaSection?.label) {
    if (
      requiredSuffix &&
      !String(schemaSection.label).endsWith(requiredSuffix)
    ) {
      addVariations(`${schemaSection.label}${requiredSuffix}`);
    } else {
      addVariations(schemaSection.label);
    }
  }
  if (schemaSection?.title) {
    if (
      requiredSuffix &&
      !String(schemaSection.title).endsWith(requiredSuffix)
    ) {
      addVariations(`${schemaSection.title}${requiredSuffix}`);
    } else {
      addVariations(schemaSection.title);
    }
  }
  if (schemaSection?.id) addVariations(schemaSection.id);
  if (schemaSection?.name) addVariations(schemaSection.name);
  if (schemaSection?.key) addVariations(schemaSection.key);

  return candidateSecKeys;
};

const cleanKeyForMatch = (k) =>
  String(k || "")
    .replace(/^\d+[_-]/, "")
    .replaceAll(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

const isSuffixValid = (pk, requiredSuffix) => {
  if (requiredSuffix) {
    const pkSuffixMatch = /(_\d+)$/.exec(pk);
    return pkSuffixMatch?.[1] === requiredSuffix;
  }
  return !/_(\d+)$/.test(pk);
};

const searchContainerListForKeys = (containers, keys) => {
  for (const c of containers) {
    if (!c || typeof c !== "object") continue;
    const v = findValInContainer(c, keys);
    if (v !== undefined && v !== null && v !== "") return unwrapValue(v);
  }
  return undefined;
};

const searchPayloadNestedForKeys = (formPayload, keys, requiredSuffix) => {
  if (!formPayload || typeof formPayload !== "object") return undefined;
  for (const [pk, pv] of Object.entries(formPayload)) {
    if (
      !pv ||
      typeof pv !== "object" ||
      Array.isArray(pv) ||
      pk.startsWith("_")
    ) {
      continue;
    }
    if (!isSuffixValid(pk, requiredSuffix)) continue;
    const v = findValInContainer(pv, keys);
    if (v !== undefined && v !== null && v !== "") return unwrapValue(v);
  }
  return undefined;
};

const collectMatchingContainers = (
  obj,
  candidateSecKeys,
  requiredSuffix,
  containers,
) => {
  if (!obj || typeof obj !== "object") return;

  for (const sKey of candidateSecKeys) {
    if (
      obj[sKey] &&
      typeof obj[sKey] === "object" &&
      !containers.includes(obj[sKey])
    ) {
      containers.push(obj[sKey]);
    }
  }

  const cleanCandidates = new Set(
    Array.from(candidateSecKeys || [])
      .map(cleanKeyForMatch)
      .filter(Boolean),
  );

  for (const pk of Object.keys(obj)) {
    if (!isSuffixValid(pk, requiredSuffix)) continue;
    const cleanPk = cleanKeyForMatch(pk);
    if (cleanPk && cleanCandidates.has(cleanPk)) {
      const val = obj[pk];
      if (val && typeof val === "object" && !containers.includes(val)) {
        containers.push(val);
      }
    }
  }
};

const getSectionCandidateContainers = (
  formPayload,
  sectionKey,
  schemaSection,
) => {
  if (!formPayload || typeof formPayload !== "object") return [];
  const containers = [];

  const secKeyStr = String(
    sectionKey || schemaSection?.key || schemaSection?.id || "",
  ).trim();
  const suffixMatch = /(_\d+)$/.exec(secKeyStr);
  const requiredSuffix = suffixMatch ? suffixMatch[1] : null;

  const candidateSecKeys = generateCandidateSectionKeys(
    sectionKey,
    schemaSection,
    requiredSuffix,
  );

  collectMatchingContainers(
    formPayload,
    candidateSecKeys,
    requiredSuffix,
    containers,
  );

  if (formPayload.sections && typeof formPayload.sections === "object") {
    collectMatchingContainers(
      formPayload.sections,
      candidateSecKeys,
      requiredSuffix,
      containers,
    );
  }

  return containers;
};

const findInSchemaFields = (schemaSection, key, formPayload, sectionKey) => {
  if (!schemaSection?.fields) return undefined;
  for (const fKey of Object.keys(schemaSection.fields)) {
    if (
      fKey.toLowerCase() === key.toLowerCase() ||
      schemaSection.fields[fKey]?.label?.toLowerCase() === key.toLowerCase()
    ) {
      const v = resolveRawFieldValue(
        schemaSection.fields[fKey],
        fKey,
        formPayload,
        sectionKey,
        schemaSection,
      );
      if (v !== undefined) return v;
    }
  }
  return undefined;
};

const findDependencyValue = (
  candidateKeys,
  formPayload,
  sectionKey,
  schemaSection,
  key,
) => {
  const sectionContainers = getSectionCandidateContainers(
    formPayload,
    sectionKey,
    schemaSection,
  );

  const containerVal = searchContainerListForKeys(
    sectionContainers,
    candidateKeys,
  );
  if (containerVal !== undefined) return containerVal;

  if (formPayload && typeof formPayload === "object") {
    const val = findValInContainer(formPayload, candidateKeys);
    if (val !== undefined) return val;

    const secKeyStr = String(
      sectionKey || schemaSection?.key || schemaSection?.id || "",
    ).trim();
    const suffixMatch = /(_\d+)$/.exec(secKeyStr);
    const requiredSuffix = suffixMatch ? suffixMatch[1] : null;

    const nestedVal = searchPayloadNestedForKeys(
      formPayload,
      candidateKeys,
      requiredSuffix,
    );
    if (nestedVal !== undefined) return nestedVal;
  }

  return findInSchemaFields(schemaSection, key, formPayload, sectionKey);
};

const isFieldDependencySatisfied = (
  dependency,
  formPayload,
  sectionKey,
  schemaSection,
) => {
  if (!dependency?.key) return true;
  const { key, value } = dependency;

  const candidateKeys = [
    key,
    key.toLowerCase(),
    key.toUpperCase(),
    key.replaceAll(/[^a-zA-Z0-9]/g, "_"),
    key.replaceAll(/[^a-zA-Z0-9]/g, "").toLowerCase(),
  ];

  const depVal = findDependencyValue(
    candidateKeys,
    formPayload,
    sectionKey,
    schemaSection,
    key,
  );

  if (depVal === undefined) return true;

  const actualDepVal = unwrapValue(depVal);
  const normActual = normalizeBooleanComparisonValue(actualDepVal);

  if (Array.isArray(value)) {
    return value.some((v) => normalizeBooleanComparisonValue(v) === normActual);
  }
  return normActual === normalizeBooleanComparisonValue(value);
};

const getSelectDefault = (field) => {
  if (!field?.options || !Array.isArray(field.options)) return undefined;
  if (field.type === "single_select") {
    const selOpt = field.options.find((o) => o.is_selected);
    return selOpt ? getEffectiveOptionLabel(selOpt) : undefined;
  }
  if (field.type === "multi_select") {
    const selected = field.options
      .filter((o) => o.is_selected)
      .map((o) => getEffectiveOptionLabel(o));
    return selected.length > 0 ? selected : undefined;
  }
  return undefined;
};

const resolveFieldSchemaDefault = (field) => {
  const selectVal = getSelectDefault(field);
  if (selectVal !== undefined) return selectVal;

  const fieldType = field?.type || "text";
  if (
    (fieldType === "boolean" || isCheckboxField(field)) &&
    field?.is_checked !== undefined &&
    field?.is_checked !== null
  ) {
    return field.is_checked;
  }

  if (
    field?.value !== undefined &&
    field?.value !== null &&
    field?.value !== "" &&
    (!Array.isArray(field.value) || field.value.length > 0)
  ) {
    return field.value;
  }

  if (
    field?.defaultValue !== undefined &&
    field?.defaultValue !== null &&
    field?.defaultValue !== ""
  ) {
    return field.defaultValue;
  }

  return undefined;
};

const findInContainerFields = (sectionContainers, candidateKeys) => {
  for (const container of sectionContainers) {
    let val = findValInContainer(container, candidateKeys);
    if (val !== undefined) return val;

    if (container?.fields && typeof container.fields === "object") {
      val = findValInContainer(container.fields, candidateKeys);
      if (val !== undefined) return val;
    }
  }
  return undefined;
};

const findRawFieldValueInPayload = (
  candidateKeys,
  sectionContainers,
  formPayload,
  sectionKey,
  schemaSection,
) => {
  const containerVal = findInContainerFields(sectionContainers, candidateKeys);
  if (containerVal !== undefined) return containerVal;

  if (!formPayload || typeof formPayload !== "object") return undefined;

  let val = findValInContainer(formPayload, candidateKeys);
  if (val !== undefined) return val;

  if (sectionKey) {
    for (const ck of candidateKeys) {
      if (formPayload[`${sectionKey}.${ck}`] !== undefined) {
        return formPayload[`${sectionKey}.${ck}`];
      }
    }
  }

  const secKeyStr = String(
    sectionKey || schemaSection?.key || schemaSection?.id || "",
  ).trim();
  const suffixMatch = /(_\d+)$/.exec(secKeyStr);
  const requiredSuffix = suffixMatch ? suffixMatch[1] : null;

  return searchPayloadNestedForKeys(formPayload, candidateKeys, requiredSuffix);
};

const resolveRawFieldValue = (
  field,
  fieldKey,
  formPayload,
  sectionKey,
  schemaSection = null,
) => {
  const candidateKeys = getFieldCandidateKeys(field, fieldKey);
  const sectionContainers = getSectionCandidateContainers(
    formPayload,
    sectionKey,
    schemaSection,
  );

  let rawVal = findRawFieldValueInPayload(
    candidateKeys,
    sectionContainers,
    formPayload,
    sectionKey,
    schemaSection,
  );

  if (rawVal === undefined || rawVal === null || rawVal === "") {
    rawVal = resolveFieldSchemaDefault(field);
  }

  if (
    rawVal &&
    typeof rawVal === "object" &&
    !Array.isArray(rawVal) &&
    rawVal.branches &&
    typeof rawVal.branches === "object"
  ) {
    return rawVal;
  }

  return unwrapValue(rawVal);
};

const extractGroupFieldItem = (
  field,
  fieldKey,
  rawVal,
  formPayload,
  sectionKey,
  schemaSection,
) => {
  if (!field.fields || typeof field.fields !== "object") return null;

  const answeredSubItems = [];
  Object.keys(field.fields).forEach((subKey) => {
    const subField = field.fields[subKey];
    let subVal;
    if (rawVal && typeof rawVal === "object" && rawVal[subKey] !== undefined) {
      subVal = rawVal[subKey];
    } else if (
      sectionKey &&
      formPayload?.[sectionKey]?.[`${fieldKey}.${subKey}`] !== undefined
    ) {
      subVal = formPayload[sectionKey][`${fieldKey}.${subKey}`];
    } else if (formPayload?.[`${fieldKey}.${subKey}`] !== undefined) {
      subVal = formPayload[`${fieldKey}.${subKey}`];
    }

    if (subVal === undefined || subVal === null || subVal === "") {
      subVal = resolveRawFieldValue(
        subField,
        subKey,
        rawVal && typeof rawVal === "object" ? rawVal : {},
        sectionKey,
        schemaSection,
      );
    }

    subVal = unwrapValue(subVal);

    if (isValueAnswered(subField, subVal)) {
      answeredSubItems.push({
        label: getEffectiveLabel(subField, subKey),
        key: subKey || subField?.key || subField?.id || subField?.name,
        answer: formatAnswer(subField, subVal),
        type: subField.type || "text",
      });
    }
  });

  if (answeredSubItems.length > 0) {
    return {
      label: getEffectiveLabel(field, fieldKey),
      key: fieldKey || field?.key || field?.id || field?.name,
      type: "group",
      subItems: answeredSubItems,
    };
  }
  return null;
};

const getObjectBranches = (obj) => {
  if (
    obj?.branches &&
    typeof obj.branches === "object" &&
    Object.keys(obj.branches).length > 0
  ) {
    return obj.branches;
  }
  return null;
};

const getActualBranchValue = (rawVal) => {
  if (!rawVal) return rawVal;
  if (Array.isArray(rawVal)) return rawVal;
  if (typeof rawVal === "object" && "value" in rawVal) return rawVal.value;
  if (rawVal.value !== undefined) return rawVal.value;
  return rawVal;
};

const findBranchesInSchema = (schemaSection, fieldKey, field) => {
  if (!schemaSection?.fields) return null;
  const sField =
    schemaSection.fields[fieldKey] ||
    Object.values(schemaSection.fields).find(
      (f) =>
        f?.key === fieldKey ||
        (f?.label &&
          field?.label &&
          f.label.trim().toLowerCase() === field.label.trim().toLowerCase()),
    );
  return getObjectBranches(sField);
};

const findBranchesInContainers = (sectionContainers, fieldCandidateKeys) => {
  for (const c of sectionContainers) {
    if (!c || typeof c !== "object") continue;
    const cVal = findValInContainer(c, fieldCandidateKeys);
    const b = getObjectBranches(cVal);
    if (b) return b;
  }
  return null;
};

const findBranchesInPayload = (formPayload, fieldCandidateKeys) => {
  if (!formPayload || typeof formPayload !== "object") return null;
  const rootVal = findValInContainer(formPayload, fieldCandidateKeys);
  const rootBranches = getObjectBranches(rootVal);
  if (rootBranches) return rootBranches;

  for (const [k, v] of Object.entries(formPayload)) {
    if (v && typeof v === "object" && !Array.isArray(v) && !k.startsWith("_")) {
      const nestedVal = findValInContainer(v, fieldCandidateKeys);
      const nestedBranches = getObjectBranches(nestedVal);
      if (nestedBranches) return nestedBranches;
    }
  }
  return null;
};

const findBranchesInPayloadOrSchema = (
  schemaSection,
  fieldKey,
  field,
  sectionContainers,
  formPayload,
  fieldCandidateKeys,
) =>
  findBranchesInSchema(schemaSection, fieldKey, field) ||
  findBranchesInContainers(sectionContainers, fieldCandidateKeys) ||
  findBranchesInPayload(formPayload, fieldCandidateKeys) ||
  null;

const normBranchStr = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]/g, "");

const findDirectSubVal = (rawVal, branches, branchKey, branchConfig) => {
  let v =
    rawVal?.branches?.[branchKey]?.subQuestion?.value ??
    rawVal?.branches?.[branchKey]?.value ??
    branchConfig?.subQuestion?.value ??
    branchConfig?.value;

  if (v !== undefined && v !== null && v !== "") return unwrapValue(v);

  const bNorm = normBranchStr(branchKey);
  const findInBranchMap = (branchMap) => {
    if (!branchMap || typeof branchMap !== "object") return undefined;
    const matchedKey = Object.keys(branchMap).find(
      (k) => normBranchStr(k) === bNorm,
    );
    if (!matchedKey) return undefined;
    const b = branchMap[matchedKey];
    return (
      b?.subQuestion?.value ??
      b?.value ??
      (typeof b === "object" ? undefined : b)
    );
  };

  v = findInBranchMap(rawVal?.branches) ?? findInBranchMap(branches);
  if (v !== undefined && v !== null && v !== "") return unwrapValue(v);
  return undefined;
};

const findSubValInBranchPatterns = (
  branchPatterns,
  sectionContainers,
  formPayload,
  sectionKey,
  requiredSuffix,
) => {
  const cVal = searchContainerListForKeys(sectionContainers, branchPatterns);
  if (cVal !== undefined) return cVal;

  let v = findValInContainer(formPayload, branchPatterns);
  if (v !== undefined && v !== null && v !== "") return unwrapValue(v);

  if (sectionKey && formPayload?.[sectionKey]) {
    v = findValInContainer(formPayload[sectionKey], branchPatterns);
    if (v !== undefined && v !== null && v !== "") return unwrapValue(v);
  }

  return searchPayloadNestedForKeys(
    formPayload,
    branchPatterns,
    requiredSuffix,
  );
};

const findSubValInSubQKeys = (
  subQKeys,
  sectionContainers,
  formPayload,
  sectionKey,
  requiredSuffix,
) => {
  const cVal = searchContainerListForKeys(sectionContainers, subQKeys);
  if (cVal !== undefined) return cVal;

  if (sectionKey && formPayload?.[sectionKey]) {
    const v = findValInContainer(formPayload[sectionKey], subQKeys);
    if (v !== undefined && v !== null && v !== "") return unwrapValue(v);
  }
  let v = findValInContainer(formPayload, subQKeys);
  if (v !== undefined && v !== null && v !== "") return unwrapValue(v);

  return searchPayloadNestedForKeys(formPayload, subQKeys, requiredSuffix);
};

const resolveSubQDefaults = (subQ) => {
  if (!subQ) return undefined;
  if (subQ.type === "single_select" && Array.isArray(subQ.options)) {
    const sel = subQ.options.find((o) => o.is_selected);
    if (sel) return getEffectiveOptionLabel(sel);
  }
  if (subQ.type === "multi_select" && Array.isArray(subQ.options)) {
    const sel = subQ.options
      .filter((o) => o.is_selected)
      .map((o) => getEffectiveOptionLabel(o));
    if (sel.length > 0) return sel;
  }
  if (subQ.type === "boolean" && subQ.is_checked !== undefined) {
    return subQ.is_checked;
  }
  if (
    subQ.defaultValue !== undefined &&
    subQ.defaultValue !== null &&
    subQ.defaultValue !== ""
  ) {
    return subQ.defaultValue;
  }
  if (subQ.value !== undefined && subQ.value !== null && subQ.value !== "") {
    return subQ.value;
  }
  return undefined;
};

const extractBranchItemValue = (b) => {
  if (!b) return undefined;
  const v =
    b?.subQuestion?.value ??
    b?.value ??
    (typeof b === "object" ? undefined : b);
  if (v !== undefined && v !== null && v !== "") return unwrapValue(v);
  return undefined;
};

const findBranchValueInField = (cField, branchKey, bNorm) => {
  if (!cField || typeof cField !== "object" || !cField.branches)
    return undefined;
  if (cField.branches[branchKey]) {
    const val = extractBranchItemValue(cField.branches[branchKey]);
    if (val !== undefined) return val;
  }
  const matched = Object.keys(cField.branches).find(
    (k) => normBranchStr(k) === bNorm,
  );
  return matched ? extractBranchItemValue(cField.branches[matched]) : undefined;
};

const findSubValInContainerBranches = (
  sectionContainers,
  fieldCandidateKeys,
  branchKey,
  bNorm,
) => {
  for (const c of sectionContainers) {
    if (!c || typeof c !== "object") continue;
    const cField = findValInContainer(c, fieldCandidateKeys);
    const v = findBranchValueInField(cField, branchKey, bNorm);
    if (v !== undefined) return v;
  }
  return undefined;
};

const resolveSubVal = ({
  rawVal,
  branches,
  branchKey,
  subQ,
  branchConfig,
  sectionContainers,
  fieldCandidateKeys,
  fieldKey,
  formPayload,
  sectionKey,
  schemaSection,
}) => {
  const direct = findDirectSubVal(rawVal, branches, branchKey, branchConfig);
  if (direct !== undefined) return direct;

  const bNorm = normBranchStr(branchKey);
  const containerBranchVal = findSubValInContainerBranches(
    sectionContainers,
    fieldCandidateKeys,
    branchKey,
    bNorm,
  );
  if (containerBranchVal !== undefined) return containerBranchVal;

  const secKeyStr = String(
    sectionKey || schemaSection?.key || schemaSection?.id || "",
  ).trim();
  const suffixMatch = /(_\d+)$/.exec(secKeyStr);
  const requiredSuffix = suffixMatch ? suffixMatch[1] : null;

  const branchPatterns = [
    `${fieldKey}_branch_${branchKey}`,
    `${fieldKey}_branch_${String(branchKey).toLowerCase()}`,
    `${fieldKey}_${branchKey}`,
    `${fieldKey}_${String(branchKey).toLowerCase()}`,
  ];

  const patternVal = findSubValInBranchPatterns(
    branchPatterns,
    sectionContainers,
    formPayload,
    sectionKey,
    requiredSuffix,
  );
  if (patternVal !== undefined) return patternVal;

  if (subQ) {
    const subQKeys = getFieldCandidateKeys(subQ, subQ.key || subQ.label);
    const subQVal = findSubValInSubQKeys(
      subQKeys,
      sectionContainers,
      formPayload,
      sectionKey,
      requiredSuffix,
    );
    if (subQVal !== undefined) return subQVal;

    return resolveSubQDefaults(subQ);
  }

  return undefined;
};

const resolveBranchSubQuestions = (
  field,
  fieldKey,
  rawVal,
  formPayload,
  sectionKey,
  schemaSection = null,
) => {
  const sectionContainers = getSectionCandidateContainers(
    formPayload,
    sectionKey,
    schemaSection,
  );
  const fieldCandidateKeys = getFieldCandidateKeys(field, fieldKey);

  let branches = getObjectBranches(field) || getObjectBranches(rawVal);

  if (!branches) {
    branches = findBranchesInPayloadOrSchema(
      schemaSection,
      fieldKey,
      field,
      sectionContainers,
      formPayload,
      fieldCandidateKeys,
    );
  }

  if (!branches) return undefined;

  const actualVal = getActualBranchValue(rawVal);

  const activeBranchKeys = new Set();

  const matchAndActivate = (candidate) => {
    if (!candidate && candidate !== false && candidate !== 0) return;
    const candNorm = normBranchStr(candidate);
    if (!candNorm) return;

    if (candNorm === "true" || candNorm === "yes") {
      ["true", "yes", "Yes"].forEach((k) => {
        if (branches[k]) activeBranchKeys.add(k);
      });
    } else if (candNorm === "false" || candNorm === "no") {
      ["false", "no", "No"].forEach((k) => {
        if (branches[k]) activeBranchKeys.add(k);
      });
    }

    Object.keys(branches).forEach((bKey) => {
      const bNorm = normBranchStr(bKey);
      if (!bNorm) return;
      if (
        bKey === candidate ||
        bNorm === candNorm ||
        bNorm.startsWith(candNorm) ||
        candNorm.startsWith(bNorm)
      ) {
        activeBranchKeys.add(bKey);
      }
    });
  };

  if (Array.isArray(actualVal)) {
    actualVal.forEach((item) => {
      if (item && typeof item === "object") {
        matchAndActivate(item.label || item.value || item.name);
      } else {
        matchAndActivate(item);
      }
    });
  } else if (
    actualVal !== undefined &&
    actualVal !== null &&
    actualVal !== ""
  ) {
    const s = String(actualVal);
    matchAndActivate(s);
    const parts = s
      .split(/[,;/\n]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    parts.forEach((p) => matchAndActivate(p));
  }

  const subValHelper = (branchKey, subQ, branchConfig) =>
    resolveSubVal({
      rawVal,
      branches,
      branchKey,
      subQ,
      branchConfig,
      sectionContainers,
      fieldCandidateKeys,
      fieldKey,
      formPayload,
      sectionKey,
      schemaSection,
    });

  // Any branch in branches that actually has an answered sub-question in the payload is activated
  Object.keys(branches).forEach((bKey) => {
    if (activeBranchKeys.has(bKey)) return;
    const branchConfig = branches[bKey] || rawVal?.branches?.[bKey];
    const subQ =
      branchConfig?.subQuestion || rawVal?.branches?.[bKey]?.subQuestion;
    if (subQ) {
      const testVal = subValHelper(bKey, subQ, branchConfig);
      if (isValueAnswered(subQ, testVal)) {
        activeBranchKeys.add(bKey);
      }
    }
  });

  if (activeBranchKeys.size === 0) return undefined;

  const results = [];
  activeBranchKeys.forEach((branchKey) => {
    const branchConfig = branches[branchKey] || rawVal?.branches?.[branchKey];
    const subQ =
      branchConfig?.subQuestion || rawVal?.branches?.[branchKey]?.subQuestion;
    if (!subQ) return;

    const subVal = subValHelper(branchKey, subQ, branchConfig);

    if (isValueAnswered(subQ, subVal)) {
      results.push({
        label:
          getEffectiveLabel(subQ, subQ.key || branchKey) ||
          branchConfig?.label ||
          "Additional Details",
        key: subQ.key || branchKey,
        answer: formatAnswer(subQ, subVal),
        type: subQ.type || "text",
        branchKey,
      });
    }
  });

  return results.length > 0 ? results : undefined;
};

const processFieldForSectionAnswered = (
  field,
  fieldKey,
  formPayload,
  sectionKey,
  schemaSection = null,
) => {
  if (field.visible === false) return null;
  if (isSpecialClinicalNoteField(fieldKey, sectionKey)) return null;

  const rawVal = resolveRawFieldValue(
    field,
    fieldKey,
    formPayload,
    sectionKey,
    schemaSection,
  );

  const dependency = field.dependency || field.visible_when;
  if (dependency) {
    const isSatisfied = isFieldDependencySatisfied(
      dependency,
      formPayload,
      sectionKey,
      schemaSection,
    );
    if (!isSatisfied && !isValueAnswered(field, rawVal)) {
      return null;
    }
  }

  const fieldType = isCheckboxField(field) ? "checkbox" : field.type || "text";

  if (fieldType === "group") {
    return extractGroupFieldItem(
      field,
      fieldKey,
      rawVal,
      formPayload,
      sectionKey,
      schemaSection,
    );
  }

  if (isValueAnswered(field, rawVal)) {
    const resolvedLabel = getEffectiveLabel(field, fieldKey);

    const item = {
      label: resolvedLabel,
      key: fieldKey || field?.key || field?.id || field?.name,
      answer: formatAnswer(field, rawVal),
      type: fieldType,
    };

    const subQuestions = resolveBranchSubQuestions(
      field,
      fieldKey,
      rawVal,
      formPayload,
      sectionKey,
      schemaSection,
    );
    if (subQuestions) {
      item.subQuestions = subQuestions;
    }
    return item;
  }

  return null;
};

const createSyntheticBranchItems = (
  fieldKey,
  rawVal,
  formPayload,
  sectionKey,
  schemaSection = null,
) => {
  const item = {
    label: formatKeyOrLabel(fieldKey, rawVal?.label),
    key: fieldKey,
    answer: formatAnswer(null, rawVal),
    type: resolveSyntheticFieldType(rawVal),
  };
  const subQuestions = resolveBranchSubQuestions(
    null,
    fieldKey,
    rawVal,
    formPayload,
    sectionKey,
    schemaSection,
  );
  if (subQuestions && subQuestions.length > 0) {
    item.subQuestions = subQuestions;
    item.hasSeparateBranchQuestions = true;
    const branchItems = subQuestions.map((sq) => ({
      label: sq.label,
      key: sq.key || sq.branchKey,
      answer: sq.answer,
      type: sq.type || "text",
      isBranch: true,
      branchKey: sq.branchKey,
    }));
    return [item, ...branchItems];
  }
  return [item];
};

const createSyntheticGroupItem = (fieldKey, rawVal) => {
  const subItems = [];
  Object.keys(rawVal).forEach((subK) => {
    if (subK.startsWith("_")) return;
    const subV = unwrapValue(rawVal[subK]);
    if (isValueAnswered(null, subV)) {
      subItems.push({
        label: formatKeyOrLabel(subK, subV?.label),
        key: subK,
        answer: formatAnswer(null, subV),
        type: resolveSyntheticFieldType(subV),
      });
    }
  });
  if (subItems.length > 0) {
    return {
      label: formatKeyOrLabel(fieldKey, rawVal?.label),
      key: fieldKey,
      type: "group",
      subItems,
    };
  }
  return {
    label: formatKeyOrLabel(fieldKey, rawVal?.label),
    key: fieldKey,
    answer: formatAnswer(null, rawVal),
    type: resolveSyntheticFieldType(rawVal),
  };
};

/**
 * Extracts answered questions from a template schema section and form payload.
 */
export const extractAnsweredQuestionsFromSection = (
  schemaSection,
  formPayload,
  sectionKey,
) => {
  if (!schemaSection?.fields) return [];

  const answered = [];
  const processedKeys = new Set();
  const schemaDefinedKeys = new Set();

  const addKeyVariants = (set, key) => {
    if (!key) return;
    const str = String(key).trim();
    if (!str) return;
    set.add(str);
    set.add(str.toLowerCase());
    const snake = trimUnderscores(
      str.replaceAll(/[^a-zA-Z0-9]+/g, "_").toLowerCase(),
    );
    if (snake) set.add(snake);
    const clean = str.replaceAll(/[^a-zA-Z0-9]/g, "").toLowerCase();
    if (clean) set.add(clean);
  };

  Object.keys(schemaSection.fields).forEach((fieldKey) => {
    addKeyVariants(schemaDefinedKeys, fieldKey);
    const field = schemaSection.fields[fieldKey];
    if (field) {
      addKeyVariants(schemaDefinedKeys, field.key);
      addKeyVariants(schemaDefinedKeys, field.id);
      addKeyVariants(schemaDefinedKeys, field.name);
      addKeyVariants(schemaDefinedKeys, field.label);
      const candidates = getFieldCandidateKeys(field, fieldKey);
      candidates.forEach((ck) => addKeyVariants(schemaDefinedKeys, ck));

      if (field.fields && typeof field.fields === "object") {
        Object.keys(field.fields).forEach((subKey) => {
          addKeyVariants(schemaDefinedKeys, subKey);
          addKeyVariants(schemaDefinedKeys, `${fieldKey}.${subKey}`);
          addKeyVariants(schemaDefinedKeys, `${fieldKey}_${subKey}`);
          const subField = field.fields[subKey];
          if (subField) {
            addKeyVariants(schemaDefinedKeys, subField.key);
            addKeyVariants(schemaDefinedKeys, subField.id);
            addKeyVariants(schemaDefinedKeys, subField.name);
            addKeyVariants(schemaDefinedKeys, subField.label);
            const subCandidates = getFieldCandidateKeys(subField, subKey);
            subCandidates.forEach((sck) => {
              addKeyVariants(schemaDefinedKeys, sck);
              addKeyVariants(schemaDefinedKeys, `${fieldKey}.${sck}`);
              addKeyVariants(schemaDefinedKeys, `${fieldKey}_${sck}`);
            });
          }
        });
      }

      if (field.branches && typeof field.branches === "object") {
        Object.keys(field.branches).forEach((branchKey) => {
          addKeyVariants(schemaDefinedKeys, `${fieldKey}_branch_${branchKey}`);
          addKeyVariants(
            schemaDefinedKeys,
            `${fieldKey}_branch_${String(branchKey).toLowerCase()}`,
          );
          addKeyVariants(schemaDefinedKeys, `${fieldKey}_${branchKey}`);
          addKeyVariants(
            schemaDefinedKeys,
            `${fieldKey}_${String(branchKey).toLowerCase()}`,
          );
          const branchSubQ = field.branches[branchKey]?.subQuestion;
          if (branchSubQ) {
            addKeyVariants(schemaDefinedKeys, branchSubQ.key);
            addKeyVariants(schemaDefinedKeys, branchSubQ.label);
            const subCandidates = getFieldCandidateKeys(
              branchSubQ,
              branchSubQ.key || branchKey,
            );
            subCandidates.forEach((sck) =>
              addKeyVariants(schemaDefinedKeys, sck),
            );
          }
        });
      }
    }
  });

  Object.keys(schemaSection.fields).forEach((fieldKey) => {
    const field = schemaSection.fields[fieldKey];

    if (
      processedKeys.has(fieldKey) ||
      (field?.key && processedKeys.has(field.key))
    ) {
      return;
    }

    addKeyVariants(processedKeys, fieldKey);
    if (field?.key) addKeyVariants(processedKeys, field.key);
    if (field?.label) addKeyVariants(processedKeys, field.label);

    const item = processFieldForSectionAnswered(
      field,
      fieldKey,
      formPayload,
      sectionKey,
      schemaSection,
    );
    if (item) {
      if (item.subQuestions && Array.isArray(item.subQuestions)) {
        item.hasSeparateBranchQuestions = true;
        item.subQuestions.forEach((sq) => {
          if (sq.label) addKeyVariants(processedKeys, sq.label);
          if (sq.key) addKeyVariants(processedKeys, sq.key);
          if (sq.branchKey) {
            addKeyVariants(processedKeys, `${fieldKey}_branch_${sq.branchKey}`);
            addKeyVariants(
              processedKeys,
              `${fieldKey}_branch_${String(sq.branchKey).toLowerCase()}`,
            );
            addKeyVariants(processedKeys, `${fieldKey}_${sq.branchKey}`);
            addKeyVariants(
              processedKeys,
              `${fieldKey}_${String(sq.branchKey).toLowerCase()}`,
            );
          }
        });
      }
      answered.push(item);
      if (item.subQuestions && Array.isArray(item.subQuestions)) {
        item.subQuestions.forEach((sq) => {
          answered.push({
            label: sq.label,
            key: sq.key || sq.branchKey,
            answer: sq.answer,
            type: sq.type || "text",
            isBranch: true,
            branchKey: sq.branchKey,
          });
        });
      }
    }
  });

  // Also include any questions that exist in formPayload under this section
  // but were not defined in schemaSection.fields (e.g. dynamically added fields)
  const candidateContainers = getSectionCandidateContainers(
    formPayload,
    sectionKey,
    schemaSection,
  );
  candidateContainers.forEach((container) => {
    if (container && typeof container === "object") {
      Object.keys(container).forEach((payloadFieldKey) => {
        if (
          payloadFieldKey.startsWith("_") ||
          payloadFieldKey === "fields" ||
          payloadFieldKey === "sections" ||
          payloadFieldKey === "label" ||
          payloadFieldKey === "title"
        ) {
          return;
        }

        const lowerK = payloadFieldKey.toLowerCase();
        const cleanK = cleanKeyForMatch(payloadFieldKey);
        if (
          schemaDefinedKeys.has(payloadFieldKey) ||
          schemaDefinedKeys.has(lowerK) ||
          schemaDefinedKeys.has(cleanK) ||
          processedKeys.has(payloadFieldKey) ||
          processedKeys.has(lowerK) ||
          processedKeys.has(cleanK) ||
          Array.from(processedKeys).some((pk) => pk.toLowerCase() === lowerK) ||
          Array.from(schemaDefinedKeys).some(
            (sk) => sk.toLowerCase() === lowerK,
          )
        ) {
          return;
        }

        const rawVal = unwrapValue(container[payloadFieldKey]);
        if (rawVal === false || rawVal === "false") {
          return;
        }

        if (isValueAnswered(null, rawVal)) {
          processedKeys.add(payloadFieldKey);
          if (typeof rawVal === "object" && !Array.isArray(rawVal)) {
            if (rawVal.branches && typeof rawVal.branches === "object") {
              answered.push(
                ...createSyntheticBranchItems(
                  payloadFieldKey,
                  rawVal,
                  formPayload,
                  sectionKey,
                  schemaSection,
                ),
              );
            } else {
              answered.push(createSyntheticGroupItem(payloadFieldKey, rawVal));
            }
          } else {
            answered.push({
              label: formatKeyOrLabel(payloadFieldKey, rawVal?.label),
              key: payloadFieldKey,
              answer: formatAnswer(null, rawVal),
              type: resolveSyntheticFieldType(rawVal),
            });
          }
        }
      });
    }
  });

  return answered;
};

/**
 * Extracts answered sections from a schema and payload.
 */
export const extractAnsweredSections = (
  schema,
  formPayload,
  categoryTitle = "",
) => {
  if (!schema?.sections) return [];
  const sections = [];
  const processedSecKeys = new Set();

  Object.keys(schema.sections).forEach((sectionKey) => {
    const section = schema.sections[sectionKey];
    processedSecKeys.add(sectionKey);
    const secKeySuffixMatch = /(_\d+)$/.exec(sectionKey);
    const secKeySuffix = secKeySuffixMatch?.[1];
    if (section?.label) {
      const labelSuffixMatch = /(_\d+)$/.exec(String(section.label));
      const labelSuffix = labelSuffixMatch?.[1];
      if (!secKeySuffix || secKeySuffix === labelSuffix) {
        processedSecKeys.add(section.label);
      }
    }
    const questions = extractAnsweredQuestionsFromSection(
      section,
      formPayload,
      sectionKey,
    );

    if (questions.length > 0) {
      const title = getSectionLabel(section, sectionKey, categoryTitle);
      sections.push({
        key: sectionKey,
        title,
        category: categoryTitle,
        questions,
      });
    }
  });

  // Also check if formPayload contains sections not defined in schema.sections
  if (formPayload && typeof formPayload === "object") {
    Object.keys(formPayload).forEach((payloadSecKey) => {
      if (
        payloadSecKey.startsWith("_") ||
        payloadSecKey === "sections" ||
        payloadSecKey === "fields" ||
        payloadSecKey === "metadata" ||
        payloadSecKey.toUpperCase().includes("METADATA") ||
        payloadSecKey.toUpperCase().includes("UI_STATE") ||
        payloadSecKey.toUpperCase().includes("UISTATE") ||
        payloadSecKey.toUpperCase().includes("OUTCOME")
      ) {
        return;
      }
      if (
        processedSecKeys.has(payloadSecKey) ||
        Array.from(processedSecKeys).some(
          (pk) => pk.toLowerCase() === payloadSecKey.toLowerCase(),
        )
      ) {
        return;
      }

      const secVal = formPayload[payloadSecKey];
      if (secVal && typeof secVal === "object" && !Array.isArray(secVal)) {
        const extraQuestions = [];
        Object.keys(secVal).forEach((fKey) => {
          if (fKey.startsWith("_")) return;
          const val = unwrapValue(secVal[fKey]);
          if (val === false || val === "false") return;
          if (!isValueAnswered(null, val)) return;

          if (typeof val === "object" && !Array.isArray(val)) {
            if (val.branches && typeof val.branches === "object") {
              extraQuestions.push(
                ...createSyntheticBranchItems(
                  fKey,
                  val,
                  formPayload,
                  payloadSecKey,
                  null,
                ),
              );
            } else {
              extraQuestions.push(createSyntheticGroupItem(fKey, val));
            }
          } else {
            extraQuestions.push({
              label: formatKeyOrLabel(fKey, val?.label),
              key: fKey,
              answer: formatAnswer(null, val),
              type: resolveSyntheticFieldType(val),
            });
          }
        });
        if (extraQuestions.length > 0) {
          sections.push({
            key: payloadSecKey,
            title: formatSectionHeaderTitle(payloadSecKey),
            category: categoryTitle,
            questions: extraQuestions,
          });
        }
      }
    });
  }

  return sections;
};

const resolveSyntheticFieldType = (val) => {
  const actualVal =
    val && typeof val === "object" && "value" in val && !Array.isArray(val)
      ? val.value
      : val;
  if (Array.isArray(actualVal)) {
    return "multi_select";
  }
  if (
    typeof actualVal === "boolean" ||
    actualVal === "true" ||
    actualVal === "false"
  ) {
    return "boolean";
  }
  if (typeof actualVal === "number") {
    return "number";
  }
  return "text";
};

/**
 * Synthesizes a fallback template schema from a form payload object.
 */
export const synthesizeSchemaFromPayload = (payloadObj) => {
  if (!payloadObj || typeof payloadObj !== "object") return null;
  const sections = {};
  const flatFields = {};

  Object.keys(payloadObj).forEach((secKey) => {
    if (secKey.startsWith("_")) return;
    const secVal = payloadObj[secKey];
    if (secVal && typeof secVal === "object" && !Array.isArray(secVal)) {
      const fields = {};
      Object.keys(secVal).forEach((fKey) => {
        const val = secVal[fKey];
        if (
          val !== undefined &&
          val !== null &&
          val !== "" &&
          val !== false &&
          val !== "false"
        ) {
          fields[fKey] = {
            label: formatKeyOrLabel(fKey, val?.label),
            key: fKey,
            type: resolveSyntheticFieldType(val),
          };
          if (val && typeof val === "object" && val.branches) {
            fields[fKey].branches = val.branches;
          }
        }
      });
      if (Object.keys(fields).length > 0) {
        sections[secKey] = {
          label: formatKeyOrLabel(secKey),
          key: secKey,
          fields,
        };
      }
    } else if (
      secVal !== undefined &&
      secVal !== null &&
      secVal !== "" &&
      secVal !== false &&
      secVal !== "false"
    ) {
      flatFields[secKey] = {
        label: formatKeyOrLabel(secKey, secVal?.label),
        key: secKey,
        type: resolveSyntheticFieldType(secVal),
      };
      if (secVal && typeof secVal === "object" && secVal.branches) {
        flatFields[secKey].branches = secVal.branches;
      }
    }
  });

  if (Object.keys(flatFields).length > 0) {
    sections["default"] = {
      label: "Assessment Details",
      fields: flatFields,
    };
  }

  return Object.keys(sections).length > 0 ? { sections } : null;
};

const resolveProgramCandidateSchema = (
  candidateKeys,
  programSchemas,
  cleanId,
  simpleClean,
) => {
  if (!programSchemas || typeof programSchemas !== "object") return null;
  if (programSchemas.sections && typeof programSchemas.sections === "object") {
    return programSchemas;
  }

  let schema = null;
  for (const k of candidateKeys) {
    if (programSchemas?.[k]?.sections) {
      schema = programSchemas[k];
      break;
    }
  }
  if (!schema && programSchemas) {
    const targetSub = (cleanId || simpleClean || "").replaceAll("_", "");
    const foundKey = Object.keys(programSchemas).find((k) => {
      const normK = k.toUpperCase().replaceAll(/[^A-Z0-9]/g, "");
      return (
        normK.includes(targetSub) ||
        Boolean(targetSub?.includes(normK.replace("PROGRAMSPECIFIC", "")))
      );
    });
    if (foundKey && programSchemas[foundKey]?.sections) {
      schema = programSchemas[foundKey];
    }
  }
  return schema;
};

const resolveProgramCandidatePayload = (
  candidateKeys,
  programPayloads,
  cleanId,
  simpleClean,
) => {
  if (!programPayloads || typeof programPayloads !== "object") return {};

  let payload = null;
  for (const k of candidateKeys) {
    if (
      programPayloads?.[k] &&
      typeof programPayloads[k] === "object" &&
      Object.keys(programPayloads[k]).length > 0
    ) {
      payload = programPayloads[k];
      break;
    }
  }
  if (!payload && programPayloads) {
    const targetSub = (cleanId || simpleClean || "").replaceAll("_", "");
    const foundKey = Object.keys(programPayloads).find((k) => {
      const normK = k.toUpperCase().replaceAll(/[^A-Z0-9]/g, "");
      return (
        normK.includes(targetSub) ||
        Boolean(targetSub?.includes(normK.replace("PROGRAMSPECIFIC", "")))
      );
    });
    if (foundKey && programPayloads[foundKey]) {
      payload = programPayloads[foundKey];
    }
  }

  // If no program-ID wrapper key matched, but programPayloads has direct section keys:
  if (!payload && Object.keys(programPayloads).length > 0) {
    const hasSectionLikeKeys = Object.keys(programPayloads).some(
      (k) =>
        !k.startsWith("_") &&
        programPayloads[k] &&
        typeof programPayloads[k] === "object",
    );
    if (hasSectionLikeKeys) {
      payload = programPayloads;
    }
  }

  return payload || {};
};

const resolveProgramCategoryTitle = ({
  prog,
  cleanId,
  simpleClean,
  programs,
  activePrograms,
  rawProgId,
  assessmentType,
}) => {
  const matchingProgramMeta = (programs || []).find((p) => {
    if (!p || typeof p !== "object") return false;
    const pid = p.programId || p.id || p.name;
    return (
      cleanProgramId(pid) === cleanId ||
      String(pid).toUpperCase().trim() ===
        String(rawProgId).toUpperCase().trim()
    );
  });

  const matchingActiveMeta = (activePrograms || []).find((ap) => {
    if (!ap || typeof ap !== "object") return false;
    const apid = ap.id || ap.programId || ap.name;
    return (
      cleanProgramId(apid) === cleanId ||
      String(apid).toUpperCase().trim() ===
        String(rawProgId).toUpperCase().trim()
    );
  });

  const isManual = Boolean(
    prog.manuallyAdded ||
    prog.isManual ||
    matchingProgramMeta?.manuallyAdded ||
    matchingProgramMeta?.isManual ||
    matchingActiveMeta?.manuallyAdded ||
    matchingActiveMeta?.isManual,
  );

  const rawProgType =
    prog.assessmentType ||
    prog.formType ||
    matchingProgramMeta?.assessmentType ||
    matchingProgramMeta?.formType ||
    matchingActiveMeta?.assessmentType ||
    matchingActiveMeta?.formType ||
    assessmentType;

  const typeLabel = isManual ? formatAssessmentTypeLabel(rawProgType) : "";
  const progDisplayName =
    prog.name || prog.displayName || prog.groupName || cleanId || simpleClean;
  const baseCategoryTitle = `${formatProgramName(progDisplayName)} Program Assessment`;

  return isManual && typeLabel
    ? `${formatProgramName(progDisplayName)} (${typeLabel}) Program Assessment`
    : baseCategoryTitle;
};

const extractEffectiveOutcomesMeta = (searchPayloads) => {
  let effectiveClinicalNotes = "";
  let effectiveSigneeName = "";
  let effectiveSignatureDate = null;

  for (const p of searchPayloads) {
    if (!p || typeof p !== "object") continue;
    if (!effectiveClinicalNotes) {
      effectiveClinicalNotes =
        p.narrative_attestation?.narrative_note ||
        p.narrative_note ||
        p.clinicalNotes ||
        p.clinicalNote ||
        p.narrative ||
        p._outcomes?.clinicalNotes ||
        p._outcomes?.clinicalNote ||
        p._outcomes?.narrative ||
        "";
    }
    if (!effectiveSigneeName || effectiveSigneeName === "N/A") {
      effectiveSigneeName =
        p.assigneeRnName ||
        p.assignee_rn_name ||
        p.narrative_attestation?.clinician_signature ||
        p.clinician_signature ||
        p.clinicianSignature ||
        p.signeeName ||
        p._outcomes?.assigneeRnName ||
        p._outcomes?.submittedBy ||
        "";
    }
    if (!effectiveSignatureDate) {
      effectiveSignatureDate =
        p.narrative_attestation?.signature_date ||
        p.signature_date ||
        p.signatureDate ||
        null;
    }
  }

  return {
    effectiveClinicalNotes,
    effectiveSigneeName,
    effectiveSignatureDate,
  };
};

const collectAllProgramCandidates = (
  activePrograms,
  programs,
  programPayloads,
) => {
  const candidates = [];
  (activePrograms || []).forEach((prog) => {
    if (prog) candidates.push(prog);
  });

  (programs || []).forEach((prog) => {
    if (!prog) return;
    const rawId =
      typeof prog === "string"
        ? prog
        : prog.programId || prog.programCode || prog.id || prog.name;
    const pName =
      typeof prog === "string"
        ? prog
        : prog.name || prog.programName || prog.displayName || rawId;
    if (rawId) {
      candidates.push({
        id: rawId,
        name: pName,
        outcome: typeof prog === "object" ? prog.outcome : undefined,
        manuallyAdded:
          typeof prog === "object"
            ? Boolean(prog.manuallyAdded || prog.isManual)
            : false,
        isManual:
          typeof prog === "object"
            ? Boolean(prog.manuallyAdded || prog.isManual)
            : false,
        assessmentType:
          typeof prog === "object"
            ? prog.assessmentType || prog.formType
            : undefined,
        formType:
          typeof prog === "object"
            ? prog.formType || prog.assessmentType
            : undefined,
      });
    }
  });

  Object.keys(programPayloads || {}).forEach((k) => {
    const upperK = k.toUpperCase();
    if (
      upperK.startsWith("_") ||
      upperK.includes("GENERAL") ||
      upperK.includes("OTHER") ||
      upperK.includes("LIFESTYLE") ||
      upperK.includes("CARE_PLAN") ||
      upperK.includes("UI_STATE") ||
      upperK.includes("OUTCOME")
    ) {
      return;
    }
    const cleanK = cleanProgramId(k);
    candidates.push({
      id: cleanK || k,
      name: formatProgramName(k),
    });
  });

  return candidates;
};

const compileUniqueProgramsList = (
  programs,
  activePrograms,
  assessmentType,
  referenceData = null,
) => {
  const seenProgKeys = new Set();
  const uniquePrograms = [];

  (programs || []).forEach((p) => {
    const rawId = p.programId || p.programCode || p.id || p.name;
    const clean = String(rawId || "")
      .toUpperCase()
      .trim();
    if (clean && !seenProgKeys.has(clean)) {
      seenProgKeys.add(clean);
      const notesVal =
        p.declinedNotes ||
        p.declinedNote ||
        p.notes ||
        p.declined_notes ||
        p.declined_note ||
        p.comment ||
        p.comments ||
        null;

      const matchingActive = (activePrograms || []).find((ap) => {
        if (!ap || typeof ap !== "object") return false;
        const apId = ap.id || ap.programId || ap.name;
        return (
          cleanProgramId(apId) === cleanProgramId(rawId) ||
          String(apId).toUpperCase().trim() === clean
        );
      });

      const isManual = Boolean(
        p.manuallyAdded ||
        p.isManual ||
        matchingActive?.manuallyAdded ||
        matchingActive?.isManual,
      );

      const resolvedAssessmentType =
        p.assessmentType ||
        p.formType ||
        matchingActive?.assessmentType ||
        matchingActive?.formType ||
        assessmentType ||
        null;

      const rawProgColor =
        getRawColor(p) ||
        getRawColor(matchingActive) ||
        resolveOutcomeColorFromReferenceData(p.outcome, referenceData) ||
        resolveOutcomeColorFromReferenceData(p.status, referenceData) ||
        null;

      uniquePrograms.push({
        ...p,
        name: p.name ? formatOutcome(p.name) : p.name,
        outcome: formatOutcome(p.outcome),
        outcomeReason: p.outcomeReason
          ? formatOutcome(p.outcomeReason)
          : p.outcomeReason,
        subReason: p.subReason ? formatOutcome(p.subReason) : p.subReason,
        declinedReason: p.declinedReason
          ? formatOutcome(p.declinedReason)
          : p.declinedReason,
        notes: notesVal,
        declinedNotes: notesVal,
        declinedNote: notesVal,
        manuallyAdded: isManual,
        isManual,
        assessmentType: resolvedAssessmentType,
        formType: resolvedAssessmentType,
        colourCode: rawProgColor,
        outcomeColour: rawProgColor,
      });
    }
  });

  return uniquePrograms;
};

const extractMemberDemographics = (memberData = {}) => {
  const memberName =
    memberData.name ||
    memberData.memberName ||
    memberData.fullName ||
    memberData.member_name ||
    [memberData.firstName, memberData.lastName].filter(Boolean).join(" ") ||
    [memberData.first_name, memberData.last_name].filter(Boolean).join(" ") ||
    memberData.member?.name ||
    memberData.member?.memberName ||
    memberData.member?.fullName ||
    [memberData.member?.firstName, memberData.member?.lastName]
      .filter(Boolean)
      .join(" ") ||
    "N/A";

  const memberDob = formatDobOrDate(
    memberData.dob ||
      memberData.birthDate ||
      memberData.dateOfBirth ||
      memberData.birth_date ||
      memberData.date_of_birth ||
      memberData.member?.dob ||
      memberData.member?.birthDate ||
      memberData.member?.dateOfBirth,
  );

  const memberId =
    memberData.subscriberNum ||
    memberData.subscriberNumber ||
    memberData.subscriber_num ||
    memberData.subscriber_number ||
    memberData.member?.subscriberNum ||
    memberData.member?.subscriberNumber ||
    memberData.member?.subscriber_num ||
    memberData.memberId ||
    memberData.member_id ||
    memberData.id ||
    memberData.member?.memberId ||
    memberData.member?.id ||
    "N/A";

  const carrier =
    memberData.carrierName ||
    memberData.carrier ||
    memberData.carrier_name ||
    memberData.member?.carrierName ||
    memberData.member?.carrier ||
    "";

  const carrierId =
    memberData.carrierId ||
    memberData.carrierCode ||
    memberData.carrierNumber ||
    memberData.carrierNum ||
    memberData.carrier_id ||
    memberData.carrier_code ||
    memberData.carrier_number ||
    memberData.member?.carrierId ||
    memberData.member?.carrierCode ||
    memberData.member?.carrierNumber ||
    memberData.member?.carrierNum ||
    memberData.member?.carrier_id ||
    memberData.member?.carrier_code ||
    memberData.member?.carrier_number ||
    "";

  const groupName =
    memberData.groupName ||
    memberData.group ||
    memberData.group_name ||
    memberData.member?.groupName ||
    memberData.member?.group ||
    "";

  const groupId =
    memberData.groupId ||
    memberData.groupNum ||
    memberData.groupNumber ||
    memberData.group_id ||
    memberData.group_num ||
    memberData.group_number ||
    memberData.rxGroup ||
    memberData.rx_group ||
    memberData.member?.groupId ||
    memberData.member?.groupNum ||
    memberData.member?.groupNumber ||
    memberData.member?.group_id ||
    memberData.member?.group_num ||
    memberData.member?.group_number ||
    memberData.member?.rxGroup ||
    memberData.member?.rx_group ||
    "";

  return {
    name: memberName,
    dob: memberDob,
    memberId,
    carrier,
    carrierId,
    groupName,
    groupId,
  };
};

/**
 * Builds structured assessment report data organized into modules and sections.
 */
export const compileAssessmentReportData = ({
  memberData = {},
  assessmentType = "INITIAL",
  completionTime = null,
  startTime = null,
  duration = null,
  status = null,
  assigneeRnName = "",
  submittedBy = "",
  signeeName = "",
  signatureDate = null,
  signatureConfirmed = true,
  programs = [],
  clinicalNotes = "",
  generalSchema = null,
  generalPayload = {},
  generalTitle = "Core Assessment",
  programSchemas = {},
  programPayloads = {},
  activePrograms = [],
  lifestyleSchema = null,
  lifestylePayload = {},
  lifestyleTitle = "Lifestyle, Barriers and Functional Assessment",
  carePlanSchema = null,
  carePlanPayload = {},
  carePlanTitle = "Care Plan, Coordination and Completion",
  referenceData = null,
}) => {
  const effectiveRefData =
    referenceData || getCachedAssessmentReferenceData?.() || null;
  const resolvedAssigneeRn =
    assigneeRnName ||
    memberData?.assigneeRnName ||
    memberData?.assignee_rn_name ||
    memberData?.member?.assigneeRnName ||
    "";
  const searchPayloads = [
    generalPayload,
    lifestylePayload,
    carePlanPayload,
    ...Object.values(programPayloads || {}),
  ];
  const outcomesMeta = extractEffectiveOutcomesMeta(searchPayloads);
  const effectiveClinicalNotes =
    clinicalNotes || outcomesMeta.effectiveClinicalNotes;
  const effectiveSigneeName =
    resolvedAssigneeRn ||
    signeeName ||
    submittedBy ||
    outcomesMeta.effectiveSigneeName;
  const effectiveSignatureDate =
    signatureDate ||
    completionTime ||
    startTime ||
    outcomesMeta.effectiveSignatureDate;

  const modules = [];
  const addModule = (categoryTitle, schema, payload) => {
    if (!schema) return;
    const answeredSecs = extractAnsweredSections(
      schema,
      payload,
      categoryTitle,
    );
    if (answeredSecs.length > 0) {
      modules.push({ category: categoryTitle, sections: answeredSecs });
    }
  };

  const effectiveGeneralTitle =
    generalTitle === "General Assessment"
      ? "Core Assessment"
      : generalTitle || "Core Assessment";
  addModule(effectiveGeneralTitle, generalSchema, generalPayload);

  const seenProgramIds = new Set();
  const allProgramCandidates = collectAllProgramCandidates(
    activePrograms,
    programs,
    programPayloads,
  );

  allProgramCandidates.forEach((prog) => {
    const rawProgId = prog.id || prog.programId || prog.code || prog.name;
    const cleanId = cleanProgramId(rawProgId);
    const simpleClean = String(rawProgId || "")
      .toUpperCase()
      .replaceAll(/_PROGRAM_SPECIFIC|_PROGRAM/gi, "")
      .replaceAll(/[^A-Z0-9_]/g, "_")
      .trim();

    const dedupKey = cleanId || simpleClean;
    if (!dedupKey || seenProgramIds.has(dedupKey)) return;
    seenProgramIds.add(dedupKey);

    const progType = String(
      prog.assessmentType || prog.formType || "",
    ).toUpperCase();
    const candidateKeys = [
      ...(progType
        ? [
            `${cleanId}_PROGRAM_SPECIFIC_${progType}`,
            `${cleanId}_${progType}`,
            `${simpleClean}_PROGRAM_SPECIFIC_${progType}`,
            `${simpleClean}_${progType}`,
          ]
        : []),
      `${cleanId}_PROGRAM_SPECIFIC`,
      `${simpleClean}_PROGRAM_SPECIFIC`,
      cleanId,
      simpleClean,
      rawProgId,
      prog.id,
      prog.name,
      String(prog.name || "")
        .toUpperCase()
        .replaceAll(/[^A-Z0-9_]/g, "_"),
      `${String(prog.name || "")
        .toUpperCase()
        .replaceAll(/[^A-Z0-9_]/g, "_")}_PROGRAM_SPECIFIC`,
    ].filter(Boolean);

    let schema = resolveProgramCandidateSchema(
      candidateKeys,
      programSchemas,
      cleanId,
      simpleClean,
    );
    let payload = resolveProgramCandidatePayload(
      candidateKeys,
      programPayloads,
      cleanId,
      simpleClean,
    );

    if (!schema && Object.keys(payload).length > 0) {
      schema = synthesizeSchemaFromPayload(payload);
    }

    const progCategoryTitle = resolveProgramCategoryTitle({
      prog,
      cleanId,
      simpleClean,
      programs,
      activePrograms,
      rawProgId,
      assessmentType,
    });

    addModule(progCategoryTitle, schema, payload);
  });

  addModule(
    lifestyleTitle || "Lifestyle, Barriers and Functional Assessment",
    lifestyleSchema,
    lifestylePayload,
  );

  addModule(
    carePlanTitle || "Care Plan, Coordination and Completion",
    carePlanSchema,
    carePlanPayload,
  );

  const sections = [];
  modules.forEach((mod) => {
    mod.sections.forEach((sec) => {
      sections.push({ ...sec, category: mod.category });
    });
  });

  const uniquePrograms = compileUniqueProgramsList(
    programs,
    activePrograms,
    assessmentType,
    effectiveRefData,
  );
  const member = extractMemberDemographics(memberData);

  return {
    member,
    assessment: {
      type: assessmentType || "INITIAL",
      status:
        status ||
        (completionTime && completionTime !== "N/A"
          ? "Completed"
          : "Draft / In Progress"),
      completionTime: completionTime ? formatDateTime(completionTime) : "N/A",
      startTime: startTime ? formatDateTime(startTime) : "N/A",
      duration: duration || "—",
      submittedBy:
        submittedBy || effectiveSigneeName || resolvedAssigneeRn || "N/A",
      signeeName:
        effectiveSigneeName || resolvedAssigneeRn || submittedBy || "",
      assigneeRnName: resolvedAssigneeRn || effectiveSigneeName || "",
      signatureDate:
        effectiveSignatureDate || completionTime || startTime || null,
      signatureConfirmed: signatureConfirmed ?? true,
      programs: uniquePrograms,
      referenceData: effectiveRefData,
      clinicalNotes: effectiveClinicalNotes || "",
    },
    referenceData: effectiveRefData,
    modules,
    sections,
  };
};

const drawField = (doc, options) => {
  const {
    label,
    value,
    x,
    y,
    maxValWidth,
    valColor = "#1B2F3F",
    valWeight = "bold",
    valSize = 9,
  } = options;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor("#6B7280");
  doc.text(label, x, y);
  const labelW = doc.getTextWidth(label);

  const valX = x + labelW + 4;
  doc.setFont("helvetica", valWeight);
  doc.setFontSize(valSize);
  doc.setTextColor(valColor);

  let valStr = String(value || "N/A").trim();
  if (maxValWidth && maxValWidth > 10) {
    if (doc.getTextWidth(valStr) > maxValWidth) {
      while (
        valStr.length > 0 &&
        doc.getTextWidth(`${valStr}...`) > maxValWidth
      ) {
        valStr = valStr.slice(0, -1);
      }
      valStr = `${valStr}...`;
    }
  }
  doc.text(valStr, valX, y);
};

/**
 * Splits words that exceed maxWidth by character so they can be wrapped by jsPDF splitTextToSize.
 */
export const breakLongWords = (doc, text, maxWidth) => {
  if (!text || !maxWidth || maxWidth <= 0) return String(text || "");
  const words = String(text).split(" ");
  const processedWords = [];

  words.forEach((word) => {
    if (!word) {
      processedWords.push("");
      return;
    }
    if (doc.getTextWidth(word) <= maxWidth) {
      processedWords.push(word);
    } else {
      let currentPiece = "";
      for (const char of word) {
        if (doc.getTextWidth(currentPiece + char) <= maxWidth) {
          currentPiece += char;
        } else {
          if (currentPiece) processedWords.push(currentPiece);
          currentPiece = char;
        }
      }
      if (currentPiece) processedWords.push(currentPiece);
    }
  });

  return processedWords.join(" ");
};

/**
 * Safely wraps text to maxWidth, breaking long unbroken words if necessary to prevent overflowing.
 */
export const splitWrappedText = (doc, text, maxWidth) => {
  if (!text || !maxWidth || maxWidth <= 0) return [];
  const safeText = breakLongWords(doc, text, maxWidth);
  return doc.splitTextToSize(safeText, maxWidth);
};

const getDisplayFieldLabel = (label, key) => {
  if (key && /_\d+$/.test(key)) {
    const leafKey = String(key).split(".").pop();
    if (leafKey && /_\d+$/.test(leafKey) && (!label || !/_\d+$/.test(label))) {
      return leafKey;
    }
  }
  return label || "Field";
};

const calcSingleFieldHeight = (doc, label, answer, type, width, extra = {}) => {
  const {
    subQuestions = [],
    hasSeparateBranchQuestions = false,
    key = "",
  } = Array.isArray(extra) ? { subQuestions: extra } : extra || {};
  const displayLabel = getDisplayFieldLabel(label, key);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const labelLines = splitWrappedText(
    doc,
    String(displayLabel || "Field"),
    width,
  );
  const labelHeight = labelLines.length * 9.5;

  const formattedAnswer = formatFieldValueForDisplay(answer, type);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const aLines = splitWrappedText(
    doc,
    String(formattedAnswer || "—"),
    width - 12,
  );
  const isMultiLine =
    type === "textarea" ||
    aLines.length > 1 ||
    (String(formattedAnswer).length > 40 && !Array.isArray(formattedAnswer));
  const boxHeight = isMultiLine ? Math.max(20, aLines.length * 11 + 6) : 20;

  let subH = 0;
  if (!hasSeparateBranchQuestions && subQuestions && subQuestions.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    subQuestions.forEach((subQ) => {
      const subLabel = getDisplayFieldLabel(subQ.label, subQ.key);
      const fullText = `> ${subLabel}: ${subQ.answer || ""}`;
      const lines = splitWrappedText(doc, fullText, width - 8);
      subH += Math.max(12, lines.length * 10 + 2);
    });
  }
  return labelHeight + 2 + boxHeight + subH;
};

const formatFieldValueForDisplay = (answer, type) => {
  const lowerAns = String(answer || "")
    .toLowerCase()
    .trim();
  if (type === "checkbox") {
    if (lowerAns === "true" || lowerAns === "yes") return "Yes";
    if (lowerAns === "completed") return "Completed";
    return "";
  }
  if (
    type === "boolean" ||
    lowerAns === "yes" ||
    lowerAns === "no" ||
    lowerAns === "true" ||
    lowerAns === "false"
  ) {
    if (lowerAns === "yes" || lowerAns === "true") return "Yes";
    if (lowerAns === "no" || lowerAns === "false") return "No";
  }
  return answer;
};

const drawSingleField = (doc, options) => {
  const {
    label,
    key,
    answer,
    type,
    x,
    y,
    width,
    subQuestions = [],
    hasSeparateBranchQuestions = false,
  } = options;

  const displayLabel = getDisplayFieldLabel(label, key);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor("#403C6F");
  const labelLines = splitWrappedText(
    doc,
    String(displayLabel || "Field"),
    width,
  );
  doc.text(labelLines, x, y + 8);
  const labelHeight = labelLines.length * 9.5;

  const boxY = y + labelHeight + 2;

  const formattedAnswer = formatFieldValueForDisplay(answer, type);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const aLines = splitWrappedText(
    doc,
    String(formattedAnswer || "—"),
    width - 12,
  );
  const isMultiLine =
    type === "textarea" ||
    aLines.length > 1 ||
    (String(formattedAnswer).length > 40 && !Array.isArray(formattedAnswer));
  const boxHeight = isMultiLine ? Math.max(20, aLines.length * 11 + 6) : 20;

  doc.setFillColor("#F9FAFB");
  doc.setDrawColor("#D1D5DB");
  doc.setLineWidth(0.6);
  doc.roundedRect(x, boxY, width, boxHeight, 3, 3, "FD");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor("#1B2F3F");

  if (isMultiLine) {
    doc.text(aLines, x + 6, boxY + 11);
  } else {
    let displayVal = aLines[0] || "";
    const maxValW = width - 12;
    if (doc.getTextWidth(displayVal) > maxValW) {
      while (
        displayVal.length > 0 &&
        doc.getTextWidth(`${displayVal}...`) > maxValW
      ) {
        displayVal = displayVal.slice(0, -1);
      }
      displayVal = `${displayVal}...`;
    }
    doc.text(displayVal, x + 6, boxY + 13.5);
  }

  let totalHeight = labelHeight + 2 + boxHeight;

  if (!hasSeparateBranchQuestions && subQuestions && subQuestions.length > 0) {
    let subY = boxY + boxHeight + 4;
    doc.setFontSize(7.5);
    subQuestions.forEach((subQ) => {
      const subLabel = getDisplayFieldLabel(subQ.label, subQ.key);
      const fullText = `> ${subLabel}: ${subQ.answer || ""}`;
      const maxW = width - 8;
      const lines = splitWrappedText(doc, fullText, maxW);
      lines.forEach((line, idx) => {
        if (idx === 0) {
          const prefix = `> ${subLabel}:`;
          const prefixW = doc.getTextWidth(prefix);
          if (prefixW < maxW - 20) {
            doc.setFont("helvetica", "bold");
            doc.setTextColor("#6B7280");
            doc.text(prefix, x + 4, subY);
            doc.setFont("helvetica", "normal");
            doc.setTextColor("#1B2F3F");
            const restOfLine = line.slice(prefix.length).trim();
            doc.text(restOfLine, x + 4 + prefixW + 3, subY);
          } else {
            doc.setFont("helvetica", "bold");
            doc.setTextColor("#6B7280");
            doc.text(line, x + 4, subY);
          }
        } else {
          doc.setFont("helvetica", "normal");
          doc.setTextColor("#1B2F3F");
          doc.text(line, x + 10, subY);
        }
        subY += 10;
        totalHeight += 10;
      });
      subY += 2;
      totalHeight += 2;
    });
  }

  return totalHeight;
};

const getGroupRawTitle = (groupQ) => {
  const isSuffixGroup =
    (groupQ.key && /_\d+$/.test(groupQ.key)) ||
    (groupQ.label && /_\d+$/.test(groupQ.label));
  if (isSuffixGroup) {
    return groupQ.label && /_\d+$/.test(groupQ.label)
      ? groupQ.label
      : groupQ.key;
  }
  return groupQ.label || groupQ.key || "Group";
};

const calcGroupCardHeight = (doc, groupQ, width) => {
  const subItems = groupQ.subItems || [];
  if (subItems.length === 0) return 0;

  const pad = 10;
  const numCols =
    subItems.length >= 4 ? 4 : Math.min(3, Math.max(1, subItems.length));
  const subGap = numCols === 4 ? 8 : 10;
  const subColW = (width - pad * 2 - (numCols - 1) * subGap) / numCols;

  const subRows = [];
  for (let i = 0; i < subItems.length; i += numCols) {
    subRows.push(subItems.slice(i, i + numCols));
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  const maxTitleW = width - pad * 2;
  const rawTitle = getGroupRawTitle(groupQ);
  const normTitle = formatSectionHeaderTitle(rawTitle, groupQ.key);
  const titleLines = splitWrappedText(doc, normTitle, maxTitleW);
  const titleExtraHeight = Math.max(0, (titleLines.length - 1) * 11);

  let totalCardHeight = 22 + titleExtraHeight;
  subRows.forEach((row) => {
    let maxH = 0;
    row.forEach((sub) => {
      const h = calcSingleFieldHeight(
        doc,
        sub.label,
        sub.answer,
        sub.type,
        subColW,
        { key: sub.key },
      );
      maxH = Math.max(maxH, h);
    });
    totalCardHeight += maxH + 8;
  });
  totalCardHeight += 6;
  return totalCardHeight;
};

const drawGroupCard = (doc, groupQ, x, y, width) => {
  const subItems = groupQ.subItems || [];
  if (subItems.length === 0) return 0;

  const cardH = calcGroupCardHeight(doc, groupQ, width);
  const pad = 10;
  const numCols =
    subItems.length >= 4 ? 4 : Math.min(3, Math.max(1, subItems.length));
  const subGap = numCols === 4 ? 8 : 10;
  const subColW = (width - pad * 2 - (numCols - 1) * subGap) / numCols;

  const subRows = [];
  for (let i = 0; i < subItems.length; i += numCols) {
    subRows.push(subItems.slice(i, i + numCols));
  }

  doc.setFillColor("#FFFFFF");
  doc.setDrawColor("#E2E2E8");
  doc.setLineWidth(0.8);
  doc.roundedRect(x, y, width, cardH, 4, 4, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor("#403C6F");
  const maxTitleW = width - pad * 2;
  const rawTitle = getGroupRawTitle(groupQ);
  const normTitle = formatSectionHeaderTitle(rawTitle, groupQ.key);
  const titleLines = splitWrappedText(doc, normTitle, maxTitleW);
  doc.text(titleLines, x + pad, y + 14);

  const titleExtraHeight = Math.max(0, (titleLines.length - 1) * 11);
  let curSubY = y + 20 + titleExtraHeight;

  subRows.forEach((row) => {
    let maxH = 0;
    row.forEach((sub, cIdx) => {
      const subX = x + pad + cIdx * (subColW + subGap);
      const h = drawSingleField(doc, {
        label: sub.label,
        key: sub.key,
        answer: sub.answer,
        type: sub.type,
        x: subX,
        y: curSubY,
        width: subColW,
      });
      maxH = Math.max(maxH, h);
    });
    curSubY += maxH + 8;
  });

  return cardH;
};

const buildStandardRowBlock = (doc, row, blockWidth) => {
  const numCols = Math.min(3, row.length);
  const colGap = 10;
  const colW = (blockWidth - (numCols - 1) * colGap) / numCols;

  let maxRowH = 0;
  row.forEach((item) => {
    const fH = calcSingleFieldHeight(
      doc,
      item.label,
      item.answer,
      item.type,
      colW,
      {
        subQuestions: item.subQuestions,
        hasSeparateBranchQuestions: item.hasSeparateBranchQuestions,
        key: item.key,
      },
    );
    maxRowH = Math.max(maxRowH, fH);
  });

  return {
    type: "standard-row",
    height: maxRowH + 10,
    draw: (bx, by) => {
      row.forEach((item, idx) => {
        const fX = bx + idx * (colW + colGap);
        drawSingleField(doc, {
          label: item.label,
          key: item.key,
          answer: item.answer,
          type: item.type,
          x: fX,
          y: by,
          width: colW,
          subQuestions: item.subQuestions,
          hasSeparateBranchQuestions: item.hasSeparateBranchQuestions,
        });
      });
    },
  };
};

const processQuestionForModuleBlocks = (
  doc,
  q,
  blockWidth,
  standardRowBuffer,
  flushBuffer,
  blocks,
) => {
  if (q.subItems && q.subItems.length > 0) {
    flushBuffer();
    const gH = calcGroupCardHeight(doc, q, blockWidth);
    blocks.push({
      type: "group-card",
      height: gH + 10,
      draw: (bx, by, bw) => {
        drawGroupCard(doc, q, bx, by, bw);
      },
    });
    return;
  }

  const hasLongSubQuestion =
    !q.hasSeparateBranchQuestions &&
    q.subQuestions &&
    q.subQuestions.length > 0 &&
    (q.subQuestions.length > 1 ||
      q.subQuestions.some(
        (sq) =>
          (sq.label?.length || 0) + (String(sq.answer || "").length || 0) > 35,
      ));

  const isFullWidth =
    q.type === "textarea" || String(q.answer).length > 80 || hasLongSubQuestion;
  if (isFullWidth) {
    flushBuffer();
    const fH = calcSingleFieldHeight(
      doc,
      q.label,
      q.answer,
      q.type,
      blockWidth,
      {
        subQuestions: q.subQuestions,
        hasSeparateBranchQuestions: q.hasSeparateBranchQuestions,
        key: q.key,
      },
    );
    blocks.push({
      type: "full-width",
      height: fH + 10,
      draw: (bx, by, bw) => {
        drawSingleField(doc, {
          label: q.label,
          key: q.key,
          answer: q.answer,
          type: q.type,
          x: bx,
          y: by,
          width: bw,
          subQuestions: q.subQuestions,
          hasSeparateBranchQuestions: q.hasSeparateBranchQuestions,
        });
      },
    });
  } else {
    standardRowBuffer.push(q);
    if (standardRowBuffer.length === 3) {
      flushBuffer();
    }
  }
};

const buildModuleBlocks = (doc, mod, parentCategoryTitle, blockWidth) => {
  const blocks = [];
  const answeredSecs = (mod.sections || []).filter(
    (sec) => sec.questions && sec.questions.length > 0,
  );

  answeredSecs.forEach((sec) => {
    const isSuffixSec =
      (sec.key && /_\d+$/.test(sec.key)) ||
      (sec.title && /_\d+$/.test(sec.title));
    let rawTitle = sec.title || sec.key || "Section";
    if (isSuffixSec) {
      rawTitle = sec.title && /_\d+$/.test(sec.title) ? sec.title : sec.key;
    }
    const normSecTitle = formatSectionHeaderTitle(rawTitle, sec.key);
    const normParent = parentCategoryTitle || "";
    if (
      normSecTitle &&
      normSecTitle.toLowerCase() !== normParent.toLowerCase()
    ) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      const maxBannerW = blockWidth - 16;
      const bannerLines = splitWrappedText(doc, normSecTitle, maxBannerW);
      const bannerRectH = Math.max(16, bannerLines.length * 10.5 + 5.5);
      const blockH = bannerRectH + 8;

      blocks.push({
        type: "section-banner",
        height: blockH,
        draw: (bx, by, bw) => {
          doc.setFillColor("#ECECF1");
          doc.setDrawColor("#D9D8E2");
          doc.setLineWidth(0.6);
          doc.roundedRect(bx, by, bw, bannerRectH, 3, 3, "FD");
          doc.setTextColor("#403C6F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8.5);
          doc.text(bannerLines, bx + 8, by + 11.5, { maxWidth: maxBannerW });
        },
      });
    }

    let standardRowBuffer = [];

    const flushBuffer = () => {
      if (standardRowBuffer.length === 0) return;
      blocks.push(buildStandardRowBlock(doc, standardRowBuffer, blockWidth));
      standardRowBuffer = [];
    };

    sec.questions.forEach((q) => {
      processQuestionForModuleBlocks(
        doc,
        q,
        blockWidth,
        standardRowBuffer,
        flushBuffer,
        blocks,
      );
    });

    flushBuffer();
  });

  return blocks;
};

const parseEmbeddedId = (rawName, rawId) => {
  let name = String(rawName || "").trim();
  let id = rawId ? String(rawId).trim() : "";
  const match = /^(.{0,300}?)\s*\(([^)]+)\)$/.exec(name);
  if (match) {
    name = match[1].trim();
    if (!id) id = match[2].trim();
  }
  if (id) {
    id = id.replace(/^\((.*)\)$/, "$1").trim();
  }
  const title = toTitleCaseWords(name);
  if (title) {
    return id ? `${title} (${id})` : title;
  }
  if (id) {
    return id;
  }
  return "";
};

const resolveCarrierAndGroupStrings = (member) => {
  const carrierNameRaw =
    member.carrier || member.carrierName || member.carrier_name || "";
  const carrierIdRaw =
    member.carrierId ||
    member.carrierCode ||
    member.carrierNumber ||
    member.carrierNum ||
    member.carrier_id ||
    member.carrier_code ||
    member.carrier_number ||
    "";

  const groupNameRaw =
    member.groupName || member.group || member.group_name || "";
  const groupIdRaw =
    member.groupId ||
    member.groupNum ||
    member.groupNumber ||
    member.group_id ||
    member.group_num ||
    member.group_number ||
    member.rxGroup ||
    member.rx_group ||
    "";

  const carrierPart = parseEmbeddedId(carrierNameRaw, carrierIdRaw);
  const groupPart = parseEmbeddedId(groupNameRaw, groupIdRaw);

  if (carrierPart && groupPart) return `${carrierPart} / ${groupPart}`;
  return carrierPart || groupPart || "N/A";
};

const resolveStatusTextAndColor = (assessment) => {
  const isCompleted =
    Boolean(assessment.completionTime) && assessment.completionTime !== "N/A";
  const rawStatus =
    assessment.status || (isCompleted ? "Completed" : "Draft / In Progress");
  const isStatusCompleted =
    String(rawStatus).toUpperCase().includes("COMPLET") || isCompleted;

  let statusText = formatOutcome(rawStatus);
  if (isStatusCompleted) {
    statusText = "Completed";
  } else if (String(rawStatus).toUpperCase().includes("DRAFT")) {
    statusText = "Draft";
  } else if (String(rawStatus).toUpperCase().includes("PROGRESS")) {
    statusText = "In Progress";
  }

  const statusColor = isStatusCompleted ? "#039855" : "#B97B00";
  return { statusText, statusColor };
};

const resolveSubmittedDisplay = (assessment, hasDuration, hasSubmittedBy) => {
  if (hasDuration) {
    return assessment.submittedBy || "N/A";
  }
  if (hasSubmittedBy && hasDuration) {
    return `${assessment.submittedBy} (${assessment.duration})`;
  }
  if (hasSubmittedBy) {
    return assessment.submittedBy;
  }
  if (hasDuration) {
    return assessment.duration;
  }
  return "N/A";
};

const drawMemberCard = (
  doc,
  member,
  assessment,
  currentY,
  marginX,
  contentWidth,
) => {
  const cardInnerLeft = marginX + 12;
  const cardInnerRight = marginX + contentWidth - 12;
  const memberCardTop = currentY;
  const memberCardHeight = 94;

  doc.setFillColor("#FFFFFF");
  doc.setDrawColor("#E8E8E9");
  doc.setLineWidth(0.8);
  doc.roundedRect(
    marginX,
    memberCardTop,
    contentWidth,
    memberCardHeight,
    4,
    4,
    "FD",
  );

  doc.setFillColor("#ECECF1");
  doc.roundedRect(marginX, memberCardTop, contentWidth, 18, 4, 4, "F");
  doc.rect(marginX, memberCardTop + 10, contentWidth, 8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor("#403C6F");
  doc.text(
    "Member Information and Assessment Details",
    marginX + 10,
    memberCardTop + 13,
  );

  const { statusText, statusColor } = resolveStatusTextAndColor(assessment);
  const typeFormatted = normalizeOutreachLabel(
    formatAssessmentTypeLabel(assessment.type) ||
      String(assessment.type || "INITIAL")
        .replaceAll("_", " ")
        .toLowerCase()
        .replaceAll(/\b\w/g, (c) => c.toUpperCase()),
  );

  const carrierGroup = resolveCarrierAndGroupStrings(member);
  const hasSubmittedBy =
    Boolean(assessment.submittedBy) && assessment.submittedBy !== "N/A";
  const hasDuration =
    Boolean(assessment.duration) &&
    assessment.duration !== "—" &&
    assessment.duration !== "N/A";

  const col1X = cardInnerLeft;
  const col2X = marginX + 175;
  const col3X = marginX + 335;
  const col2X_wide = marginX + 195;

  const memberRow1Y = memberCardTop + 32;
  drawField(doc, {
    label: "Member Name:",
    value: member.name,
    x: col1X,
    y: memberRow1Y,
    maxValWidth: col2X - col1X - 65,
    valColor: "#1B2F3F",
    valWeight: "bold",
    valSize: 9.5,
  });
  drawField(doc, {
    label: "Date of Birth:",
    value: member.dob,
    x: col2X,
    y: memberRow1Y,
    maxValWidth: col3X - col2X - 58,
  });
  drawField(doc, {
    label: "Member ID:",
    value: member.memberId,
    x: col3X,
    y: memberRow1Y,
    maxValWidth: cardInnerRight - col3X - 50,
  });

  const memberRow2Y = memberCardTop + 48;
  drawField(doc, {
    label: "Carrier / Group:",
    value: carrierGroup,
    x: col1X,
    y: memberRow2Y,
    maxValWidth: cardInnerRight - col1X - 80,
    valColor: "#1B2F3F",
    valWeight: "bold",
    valSize: 8.5,
  });

  const memberRow3Y = memberCardTop + 64;
  drawField(doc, {
    label: "Assessment Type:",
    value: typeFormatted,
    x: col1X,
    y: memberRow3Y,
    maxValWidth: col2X_wide - col1X - 82,
    valColor: "#1B2F3F",
    valWeight: "bold",
    valSize: 8.5,
  });
  drawField(doc, {
    label: "Status:",
    value: statusText,
    x: col2X_wide,
    y: memberRow3Y,
    maxValWidth: hasDuration
      ? col3X - col2X_wide - 38
      : cardInnerRight - col2X_wide - 38,
    valColor: statusColor,
    valWeight: "bold",
    valSize: 8.5,
  });
  if (hasDuration) {
    drawField(doc, {
      label: "Duration:",
      value: assessment.duration,
      x: col3X,
      y: memberRow3Y,
      maxValWidth: cardInnerRight - col3X - 45,
      valColor: "#1B2F3F",
      valWeight: "normal",
      valSize: 8.5,
    });
  }

  const memberRow4Y = memberCardTop + 80;
  drawField(doc, {
    label: "Started At:",
    value: assessment.startTime,
    x: col1X,
    y: memberRow4Y,
    maxValWidth: col2X - col1X - 52,
    valColor: "#1B2F3F",
    valWeight: "normal",
    valSize: 8.5,
  });
  drawField(doc, {
    label: "Completed At:",
    value: assessment.completionTime,
    x: col2X,
    y: memberRow4Y,
    maxValWidth: col3X - col2X - 68,
    valColor: "#1B2F3F",
    valWeight: "normal",
    valSize: 8.5,
  });

  const submittedValue = resolveSubmittedDisplay(
    assessment,
    hasDuration,
    hasSubmittedBy,
  );
  drawField(doc, {
    label: "Submitted By:",
    value: submittedValue,
    x: col3X,
    y: memberRow4Y,
    maxValWidth: cardInnerRight - col3X - 68,
    valColor: "#1B2F3F",
    valWeight: "normal",
    valSize: 8.5,
  });

  return memberCardTop + memberCardHeight + 12;
};

export const getRawColor = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  const rawColor =
    obj.colourCode ||
    obj.colour_code ||
    obj.outcomeColour ||
    obj.outcomeColor ||
    obj.outcome_colour ||
    obj.outcome_color ||
    obj.outcomeColorCode ||
    obj.colorCode ||
    obj.color_code ||
    obj.color ||
    obj.statusColor ||
    obj.statusColour;
  if (rawColor) {
    const trimmed = String(rawColor).trim();
    if (/^[0-9A-Fa-f]{3,8}$/.test(trimmed)) {
      return `#${trimmed}`;
    }
    return trimmed;
  }
  return null;
};

export const resolveOutcomeColorFromReferenceData = (
  outcomeVal,
  referenceData,
) => {
  if (!outcomeVal) return null;
  const ref = referenceData || getCachedAssessmentReferenceData?.() || null;
  if (!ref?.outcomes || !Array.isArray(ref.outcomes)) {
    return null;
  }
  const norm = String(outcomeVal).toUpperCase().trim();
  const matched = ref.outcomes.find(
    (o) =>
      o.outcomeName === outcomeVal ||
      o.displayName === outcomeVal ||
      o.outcome === outcomeVal ||
      String(o.outcomeName || "")
        .toUpperCase()
        .trim() === norm ||
      String(o.displayName || "")
        .toUpperCase()
        .trim() === norm ||
      String(o.outcome || "")
        .toUpperCase()
        .trim() === norm,
  );
  return getRawColor(matched);
};

export const isEnrolledStatus = (rawOutcome, rawOutcomeStr) =>
  !rawOutcomeStr.includes("NOT") &&
  !rawOutcomeStr.includes("NON") &&
  !isProgramCompletionOutcome(rawOutcome) &&
  (isEnrolledOutcome(rawOutcome) ||
    rawOutcomeStr === "ENROLLED" ||
    rawOutcomeStr === "ENROLLED/PROCEEDING" ||
    rawOutcomeStr === "ENROLLED / PROCEEDING" ||
    rawOutcomeStr === "COMPLETED" ||
    rawOutcomeStr === "COMPLETE" ||
    (rawOutcomeStr.startsWith("ENROLL") &&
      !rawOutcomeStr.includes("PROGRESS")));

export const isDeclinedStatus = (rawOutcome, rawOutcomeStr) =>
  !isProgramCompletionOutcome(rawOutcome) &&
  (isDeclinedOutcome(rawOutcome) ||
    rawOutcomeStr.includes("DECLIN") ||
    rawOutcomeStr.includes("REFUS") ||
    rawOutcomeStr.includes("NOT_ENROLL") ||
    rawOutcomeStr.includes("NOT ENROLL"));

const buildSubReasonText = (rawReason, rawNotes) => {
  let subReasonText = "";
  if (rawReason && rawReason !== "N/A" && rawReason !== "—") {
    subReasonText = formatOutcome(rawReason);
    if (
      rawNotes &&
      String(rawNotes).trim() &&
      String(rawNotes).trim() !== String(rawReason).trim()
    ) {
      subReasonText = `${subReasonText} (${String(rawNotes).trim()})`;
    }
  } else if (rawNotes && String(rawNotes).trim()) {
    subReasonText = String(rawNotes).trim();
  } else {
    subReasonText = "—";
  }
  if (!subReasonText || subReasonText === "N/A") {
    subReasonText = "—";
  }
  return subReasonText;
};

export const getOutcomeStatusColor = (
  optionsOrIsEnrolled,
  isDeclinedLegacy,
) => {
  if (typeof optionsOrIsEnrolled === "boolean") {
    if (optionsOrIsEnrolled) return "#039855";
    return isDeclinedLegacy ? "#D92D20" : "#403C6F";
  }

  const {
    prog = {},
    rawOutcome = "",
    rawOutcomeStr = "",
    referenceData = null,
  } = optionsOrIsEnrolled || {};

  // 1. Dynamic color from program object (colourCode, outcomeColour, etc.)
  const progColor = getRawColor(prog);
  if (progColor) return progColor;

  // 2. Dynamic color from referenceData if available
  const refColor = resolveOutcomeColorFromReferenceData(
    rawOutcome,
    referenceData,
  );
  if (refColor) return refColor;

  // 3. Fallback logic:
  // If Program Completion, and no dynamic color provided in API/refData:
  if (isProgramCompletionOutcome(rawOutcome, referenceData)) {
    return "#403C6F";
  }

  const normOutcomeStr =
    rawOutcomeStr ||
    String(rawOutcome || "")
      .toUpperCase()
      .trim();
  const isEnrolled = isEnrolledStatus(rawOutcome, normOutcomeStr);
  if (isEnrolled) return "#039855";

  const isDeclined = isDeclinedStatus(rawOutcome, normOutcomeStr);
  if (isDeclined) return "#D92D20";

  return "#403C6F";
};

const drawProgramOutcomesCard = ({
  doc,
  programs,
  referenceData = null,
  currentY,
  marginX,
  contentWidth,
  pageHeight,
  bottomMargin,
}) => {
  const col1Width = 140;
  const col2Width = 115;
  const col3Width = 95;
  const col4Width = contentWidth - 375 - 10;

  const col1X = marginX + 10;
  const col2X = marginX + 155;
  const col3X = marginX + 275;
  const col4X = marginX + 375;

  const rowDataList = (programs || []).map((prog) => {
    const rawProgName =
      prog.name || prog.programName || prog.programId || "Program";
    const progName = formatOutcome(rawProgName);

    const rawType = prog.assessmentType || prog.formType || "";
    const typeLabel =
      formatAssessmentTypeLabel(rawType) ||
      (rawType && rawType !== "N/A" && rawType !== "NA"
        ? formatOutcome(rawType)
        : "—");

    const rawOutcome = prog.outcome || prog.status || "N/A";
    const statusLabel = formatOutcome(rawOutcome);
    const rawOutcomeStr = String(rawOutcome || "")
      .toUpperCase()
      .trim();

    const statusColor = getOutcomeStatusColor({
      prog,
      rawOutcome,
      rawOutcomeStr,
      referenceData,
    });

    const rawReason =
      prog.subReason ||
      prog.outcomeReason ||
      prog.declinedReason ||
      prog.reason;
    const rawNotes =
      prog.declinedNotes ||
      prog.declinedNote ||
      prog.notes ||
      prog.declined_notes ||
      prog.declined_note ||
      prog.comments ||
      prog.comment;

    const subReasonText = buildSubReasonText(rawReason, rawNotes);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const col1Lines = splitWrappedText(doc, progName, col1Width - 10);
    const col2Lines = splitWrappedText(doc, typeLabel || "—", col2Width - 10);
    const col3Lines = splitWrappedText(doc, statusLabel, col3Width - 10);
    const col4Lines = splitWrappedText(doc, subReasonText, col4Width - 10);

    const maxLines = Math.max(
      col1Lines.length,
      col2Lines.length,
      col3Lines.length,
      col4Lines.length,
    );
    const rowHeight = Math.max(20, maxLines * 11 + 6);

    return {
      col1Lines,
      col2Lines,
      col3Lines,
      col4Lines,
      statusColor,
      rowHeight,
    };
  });

  const headerBandH = 18;
  const tableHeaderH = 18;
  const totalRowsHeight = rowDataList.reduce((acc, r) => acc + r.rowHeight, 0);
  const progSummaryCardHeight =
    headerBandH + tableHeaderH + totalRowsHeight + 4;

  let curYPos = currentY;
  if (curYPos + progSummaryCardHeight + 10 > pageHeight - bottomMargin) {
    doc.addPage();
    curYPos = 36;
  }

  const progSummaryTop = curYPos;

  // Card Container
  doc.setFillColor("#FFFFFF");
  doc.setDrawColor("#E8E8E9");
  doc.setLineWidth(0.8);
  doc.roundedRect(
    marginX,
    progSummaryTop,
    contentWidth,
    progSummaryCardHeight,
    4,
    4,
    "FD",
  );

  // Card Header Band
  doc.setFillColor("#ECECF1");
  doc.roundedRect(
    marginX,
    progSummaryTop,
    contentWidth,
    headerBandH,
    4,
    4,
    "F",
  );
  doc.rect(marginX, progSummaryTop + 10, contentWidth, headerBandH - 10, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor("#403C6F");
  doc.text("Program Outcomes", marginX + 10, progSummaryTop + 13);

  // Table Column Header Bar
  const tableHeaderY = progSummaryTop + headerBandH;
  doc.setFillColor("#F7F8FB");
  doc.rect(marginX, tableHeaderY, contentWidth, tableHeaderH, "F");
  doc.setDrawColor("#E8E8E9");
  doc.setLineWidth(0.8);
  doc.line(
    marginX,
    tableHeaderY + tableHeaderH,
    marginX + contentWidth,
    tableHeaderY + tableHeaderH,
  );

  // Table Column Headers
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor("#403C6F");
  doc.text("Program Name", col1X, tableHeaderY + 12);
  doc.text("Assessment Type", col2X, tableHeaderY + 12);
  doc.text("Status", col3X, tableHeaderY + 12);
  doc.text("Subreason", col4X, tableHeaderY + 12);

  // Render Data Rows
  let rowY = tableHeaderY + tableHeaderH;
  rowDataList.forEach((row, idx) => {
    // Subtle alternating row fill
    if (idx % 2 === 1) {
      doc.setFillColor("#FAFAFC");
      doc.rect(marginX, rowY, contentWidth, row.rowHeight, "F");
    }

    // Column 1: Program Name
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor("#1B2F3F");
    doc.text(row.col1Lines, col1X, rowY + 12);

    // Column 2: Assessment Type
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor("#475467");
    doc.text(row.col2Lines, col2X, rowY + 12);

    // Column 3: Status
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(row.statusColor);
    doc.text(row.col3Lines, col3X, rowY + 12);

    // Column 4: Subreason
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor("#4B5563");
    doc.text(row.col4Lines, col4X, rowY + 12);

    // Row separator line (if not last row)
    if (idx < rowDataList.length - 1) {
      doc.setDrawColor("#F2F4F7");
      doc.setLineWidth(0.5);
      doc.line(
        marginX,
        rowY + row.rowHeight,
        marginX + contentWidth,
        rowY + row.rowHeight,
      );
    }

    rowY += row.rowHeight;
  });

  return progSummaryTop + progSummaryCardHeight + 14;
};

const renderNarrativeSection = ({
  doc,
  narrativeText,
  currentY,
  marginX,
  contentWidth,
  pageWidth,
  pageHeight,
  innerW,
  cardPad,
}) => {
  let curY = currentY;
  if (curY + 50 > pageHeight - 45) {
    doc.addPage();
    curY = 36;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor("#403C6F");
  doc.text("Clinical Narrative", marginX, curY + 10);
  curY += 14;
  doc.setDrawColor("#403C6F");
  doc.setLineWidth(1);
  doc.line(marginX, curY, pageWidth - marginX, curY);
  curY += 12;

  const rawNoteLines = narrativeText
    ? splitWrappedText(doc, narrativeText, innerW - 20)
    : ["No clinical narrative recorded for this assessment."];

  let lineIdx = 0;
  let isNarrativeContinuation = false;

  while (lineIdx < rawNoteLines.length) {
    const pageBottom = pageHeight - 36;
    const headerBandH = 18;
    const topPad = 12;
    const bottomPad = 10;
    const overhead = headerBandH + topPad + bottomPad;

    if (curY + overhead + 22 > pageBottom) {
      doc.addPage();
      curY = 36;
    }

    const availableH = pageBottom - curY - overhead;
    const maxLinesOnPage = Math.max(1, Math.floor(availableH / 11.5));
    const linesToTake = rawNoteLines.slice(lineIdx, lineIdx + maxLinesOnPage);
    lineIdx += linesToTake.length;

    const blockH = linesToTake.length * 11.5;
    const cardH = overhead + blockH;
    const cardTop = curY;

    doc.setFillColor("#FFFFFF");
    doc.setDrawColor("#E8E8E9");
    doc.setLineWidth(0.8);
    doc.roundedRect(marginX, cardTop, contentWidth, cardH, 4, 4, "FD");

    doc.setFillColor("#ECECF1");
    doc.roundedRect(marginX, cardTop, contentWidth, headerBandH, 4, 4, "F");
    doc.rect(marginX, cardTop + 10, contentWidth, headerBandH - 10, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor("#403C6F");
    doc.text(
      isNarrativeContinuation ? "Clinical Notes (Continued)" : "Clinical Notes",
      marginX + 10,
      cardTop + 13,
    );

    let textY = cardTop + headerBandH + topPad + 2;
    doc.setFont("helvetica", narrativeText ? "normal" : "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(narrativeText ? "#1B2F3F" : "#6B7280");
    linesToTake.forEach((line) => {
      doc.text(line, marginX + cardPad, textY);
      textY += 11.5;
    });

    curY = cardTop + cardH + 12;

    if (lineIdx < rawNoteLines.length) {
      isNarrativeContinuation = true;
      doc.addPage();
      curY = 36;
    }
  }

  return curY + 4;
};

const renderDigitalSignatureSection = ({
  doc,
  assessment,
  currentY,
  marginX,
  contentWidth,
  pageWidth,
  pageHeight,
}) => {
  const signCardH = 78;
  const signSectionNeededH = 26 + signCardH + 10;
  let curY = currentY;
  if (curY + signSectionNeededH > pageHeight - 45) {
    doc.addPage();
    curY = 36;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor("#403C6F");
  doc.text("Nurse Digital Signature", marginX, curY + 10);
  curY += 14;
  doc.setDrawColor("#403C6F");
  doc.setLineWidth(1);
  doc.line(marginX, curY, pageWidth - marginX, curY);
  curY += 12;

  const signCardTop = curY;
  doc.setFillColor("#FAFAFD");
  doc.setDrawColor("#E8E8E9");
  doc.setLineWidth(0.8);
  doc.roundedRect(marginX, signCardTop, contentWidth, signCardH, 4, 4, "FD");

  doc.setFillColor("#403C6F");
  doc.roundedRect(marginX, signCardTop, 3.5, signCardH, 2, 2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor("#403C6F");
  doc.text("Electronic Signature Verification", marginX + 14, signCardTop + 15);

  const metaY = signCardTop + 30;
  const signCol1X = marginX + 14;
  const signCol2X = marginX + 220;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor("#7F7C9F");
  doc.text("SIGNEE NAME", signCol1X, metaY);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor("#10100F");
  const displaySignee = getEffectiveSigneeName(
    assessment.assigneeRnName || assessment.signeeName,
    assessment.submittedBy,
    assessment.assigneeRnName,
  );
  doc.text(displaySignee, signCol1X, metaY + 11);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor("#7F7C9F");
  doc.text("DATE / TIME", signCol2X, metaY);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor("#10100F");
  const displayDate = getEffectiveSignatureDate(
    assessment.signatureDate,
    assessment.completionTime,
    assessment.startTime,
  );
  doc.text(displayDate, signCol2X, metaY + 11);

  doc.setDrawColor("#E8E8E9");
  doc.setLineWidth(0.6);
  doc.line(signCol1X, metaY + 18, marginX + contentWidth - 14, metaY + 18);

  const attestY = metaY + 30;
  const boxSize = 8.5;
  const boxX = signCol1X;
  const boxY = attestY - 7;

  doc.setFillColor("#403C6F");
  doc.roundedRect(boxX, boxY, boxSize, boxSize, 1.5, 1.5, "F");

  doc.setDrawColor("#FFFFFF");
  doc.setLineWidth(1.2);
  doc.line(boxX + 2, boxY + 4.2, boxX + 3.8, boxY + 6.5);
  doc.line(boxX + 3.8, boxY + 6.5, boxX + 6.8, boxY + 2.2);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor("#403C6F");
  doc.text(
    "I certify that the documentation provided is accurate and reflects the clinical assessment performed.",
    boxX + boxSize + 6,
    attestY,
  );

  return signCardTop + signCardH + 16;
};

const renderSingleCardBatch = ({
  doc,
  currentBatch,
  batchContentH,
  overhead,
  hasHeaderBand,
  headerBandTitle,
  isContinuation,
  currentY,
  marginX,
  contentWidth,
  headerBandH,
  topPad,
  cardPad,
  innerW,
}) => {
  const cardHeight = overhead + batchContentH;
  const cardTop = currentY;

  doc.setFillColor("#FFFFFF");
  doc.setDrawColor("#E8E8E9");
  doc.setLineWidth(0.8);
  doc.roundedRect(marginX, cardTop, contentWidth, cardHeight, 4, 4, "FD");

  if (hasHeaderBand) {
    doc.setFillColor("#ECECF1");
    doc.roundedRect(marginX, cardTop, contentWidth, headerBandH, 4, 4, "F");
    doc.rect(marginX, cardTop + 10, contentWidth, headerBandH - 10, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor("#403C6F");
    const cardTitle = isContinuation
      ? `${toTitleCase(headerBandTitle)} (Continued)`
      : toTitleCase(headerBandTitle);
    const maxTitleW = contentWidth - 20;
    const titleLines = splitWrappedText(doc, cardTitle, maxTitleW);
    let displayTitle = titleLines[0] || cardTitle;
    if (titleLines.length > 1) {
      while (
        displayTitle.length > 0 &&
        doc.getTextWidth(`${displayTitle}...`) > maxTitleW
      ) {
        displayTitle = displayTitle.slice(0, -1);
      }
      displayTitle = `${displayTitle}...`;
    }
    doc.text(displayTitle, marginX + 10, cardTop + 13);
  }

  let blockY = cardTop + headerBandH + topPad;
  const cardInnerX = marginX + cardPad;

  currentBatch.forEach((b) => {
    b.draw(cardInnerX, blockY, innerW);
    blockY += b.height;
  });

  return cardTop + cardHeight + 12;
};

const collectCardBatch = (blocks, startIndex, curY, pageBottom, overhead) => {
  let blockIndex = startIndex;
  const currentBatch = [];
  let batchContentH = 0;

  while (blockIndex < blocks.length) {
    const nextBlock = blocks[blockIndex];
    if (
      currentBatch.length > 0 &&
      curY + overhead + batchContentH + nextBlock.height > pageBottom
    ) {
      break;
    }
    currentBatch.push(nextBlock);
    batchContentH += nextBlock.height;
    blockIndex++;
  }
  return { currentBatch, batchContentH, nextIndex: blockIndex };
};

const renderCardBlocks = ({
  doc,
  blocks,
  hasHeaderBand,
  headerBandTitle = "",
  currentY,
  marginX,
  contentWidth,
  pageHeight,
  cardPad,
  innerW,
}) => {
  if (!blocks || blocks.length === 0) return currentY;

  let blockIndex = 0;
  let isContinuation = false;
  let curY = currentY;

  while (blockIndex < blocks.length) {
    const pageBottom = pageHeight - 36;
    const headerBandH = hasHeaderBand ? 18 : 0;
    const topPad = 12;
    const bottomPad = 10;
    const overhead = headerBandH + topPad + bottomPad;

    if (curY + overhead + blocks[blockIndex].height > pageBottom) {
      doc.addPage();
      curY = 36;
    }

    const { currentBatch, batchContentH, nextIndex } = collectCardBatch(
      blocks,
      blockIndex,
      curY,
      pageBottom,
      overhead,
    );
    blockIndex = nextIndex;

    curY = renderSingleCardBatch({
      doc,
      currentBatch,
      batchContentH,
      overhead,
      hasHeaderBand,
      headerBandTitle,
      isContinuation,
      currentY: curY,
      marginX,
      contentWidth,
      headerBandH,
      topPad,
      cardPad,
      innerW,
    });

    if (blockIndex < blocks.length) {
      isContinuation = true;
      doc.addPage();
      curY = 36;
    }
  }

  return curY;
};

const renderMainSections = ({
  doc,
  mainSections,
  currentY,
  marginX,
  contentWidth,
  pageWidth,
  pageHeight,
  innerW,
  cardPad,
}) => {
  let curY = currentY;

  mainSections.forEach((mainSec) => {
    const hasAnyQuestions = mainSec.modules.some((mod) =>
      (mod.sections || []).some(
        (sec) => sec.questions && sec.questions.length > 0,
      ),
    );
    if (!hasAnyQuestions) return;

    if (curY + 40 > pageHeight - 45) {
      doc.addPage();
      curY = 36;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor("#403C6F");
    doc.text(mainSec.title, marginX, curY);

    curY += 4;
    doc.setDrawColor("#403C6F");
    doc.setLineWidth(1);
    doc.line(marginX, curY, pageWidth - marginX, curY);
    curY += 12;

    if (mainSec.isProgramSpecific) {
      mainSec.modules.forEach((mod) => {
        const progBlocks = buildModuleBlocks(doc, mod, mod.category, innerW);
        if (progBlocks.length === 0) return;

        curY = renderCardBlocks({
          doc,
          blocks: progBlocks,
          hasHeaderBand: true,
          headerBandTitle: mod.category,
          currentY: curY,
          marginX,
          contentWidth,
          pageHeight,
          cardPad,
          innerW,
        });
      });
    } else {
      const allBlocks = [];
      mainSec.modules.forEach((mod) => {
        const modBlocks = buildModuleBlocks(doc, mod, mainSec.title, innerW);
        allBlocks.push(...modBlocks);
      });

      if (allBlocks.length > 0) {
        curY = renderCardBlocks({
          doc,
          blocks: allBlocks,
          hasHeaderBand: false,
          headerBandTitle: mainSec.title,
          currentY: curY,
          marginX,
          contentWidth,
          pageHeight,
          cardPad,
          innerW,
        });
      }
    }

    curY += 6;
  });

  return curY;
};

const drawPdfHeader = (doc, currentY, marginX, pageWidth) => {
  doc.setFillColor("#403C6F");
  doc.rect(0, 0, pageWidth, 5, "F");

  doc.setTextColor("#403C6F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.text("Clinical Assessment Report", marginX, currentY + 14);

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor("#6B7280");
  const now = new Date();
  const genDateStr = `Generated on ${now.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })} ${formatTimeOnly(now)}`;
  doc.text(genDateStr, marginX, currentY + 28);

  const logoWidth = 56;
  const logoHeight = 32;
  const logoX = pageWidth - marginX - logoWidth;
  const logoY = currentY - 2;

  try {
    doc.addImage(LUCY_LOGO_BASE64, "PNG", logoX, logoY, logoWidth, logoHeight);
  } catch (err) {
    console.debug("Logo image fallback:", err);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor("#403C6F");
    doc.text("LucyRx", pageWidth - marginX, currentY + 16, { align: "right" });
  }

  const nextY = currentY + 38;
  doc.setDrawColor("#E8E8E9");
  doc.setLineWidth(1);
  doc.line(marginX, nextY, pageWidth - marginX, nextY);
  return nextY + 12;
};

const drawRunningFooter = (doc, marginX, pageWidth, pageHeight) => {
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p += 1) {
    doc.setPage(p);

    doc.setDrawColor("#E8E8E9");
    doc.setLineWidth(0.8);
    doc.line(marginX, pageHeight - 30, pageWidth - marginX, pageHeight - 30);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor("#9CA3AF");
    doc.text(
      `This report contains confidential information. \u00A9 ${new Date().getFullYear()} LucyRx. All rights reserved.`,
      marginX,
      pageHeight - 18,
    );

    doc.setFont("helvetica", "normal");
    doc.text(
      `Page ${p} of ${totalPages}`,
      pageWidth - marginX,
      pageHeight - 18,
      { align: "right" },
    );
  }
};

/**
 * Generates and downloads a clinical assessment PDF form using jsPDF.
 */
export const generateClinicalAssessmentPdf = (reportData) => {
  const { member, assessment, modules, sections } = reportData;

  const doc = new jsPDF("p", "pt", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 36;
  const contentWidth = pageWidth - marginX * 2;
  const bottomMargin = 45;
  const cardPad = 12;
  const innerW = contentWidth - cardPad * 2;

  let currentY = drawPdfHeader(doc, 24, marginX, pageWidth);

  currentY = drawMemberCard(
    doc,
    member,
    assessment,
    currentY,
    marginX,
    contentWidth,
  );

  if (assessment.programs && assessment.programs.length > 0) {
    currentY = drawProgramOutcomesCard({
      doc,
      programs: assessment.programs,
      referenceData:
        assessment.referenceData || reportData?.referenceData || null,
      currentY,
      marginX,
      contentWidth,
      pageHeight,
      bottomMargin,
    });
  }

  let modulesToRender = modules && modules.length > 0 ? modules : [];
  if (modulesToRender.length === 0 && sections && sections.length > 0) {
    const catMap = new Map();
    sections.forEach((sec) => {
      const cat = sec.category || "Assessment Sections";
      if (!catMap.has(cat)) {
        catMap.set(cat, []);
      }
      catMap.get(cat).push(sec);
    });
    catMap.forEach((secs, cat) => {
      modulesToRender.push({ category: cat, sections: secs });
    });
  }

  const coreModules = [];
  const programModules = [];
  const lifestyleModules = [];
  const carePlanModules = [];

  modulesToRender.forEach((mod) => {
    const cat = String(mod.category || "").toLowerCase();
    if (cat.includes("general") || cat.includes("core")) {
      coreModules.push(mod);
    } else if (cat.includes("lifestyle")) {
      lifestyleModules.push(mod);
    } else if (cat.includes("care plan")) {
      carePlanModules.push(mod);
    } else {
      programModules.push(mod);
    }
  });

  const mainSections = [
    {
      title: "Core Assessment",
      modules: coreModules,
      isProgramSpecific: false,
    },
    {
      title: "Program-Specific Assessment",
      modules: programModules,
      isProgramSpecific: true,
    },
    {
      title: "Lifestyle, Barriers and Functional Assessment",
      modules: lifestyleModules,
      isProgramSpecific: false,
    },
    {
      title: "Care Plan, Coordination and Completions",
      modules: carePlanModules,
      isProgramSpecific: false,
    },
  ];

  currentY = renderMainSections({
    doc,
    mainSections,
    currentY,
    marginX,
    contentWidth,
    pageWidth,
    pageHeight,
    innerW,
    cardPad,
  });

  const narrativeText =
    assessment.clinicalNotes && String(assessment.clinicalNotes).trim();
  currentY = renderNarrativeSection({
    doc,
    narrativeText,
    currentY,
    marginX,
    contentWidth,
    pageWidth,
    pageHeight,
    innerW,
    cardPad,
  });

  renderDigitalSignatureSection({
    doc,
    assessment,
    currentY,
    marginX,
    contentWidth,
    pageWidth,
    pageHeight,
  });

  drawRunningFooter(doc, marginX, pageWidth, pageHeight);

  const sanitizedName = String(member.name || "Member")
    .replaceAll(/[^a-zA-Z0-9_-]/g, "_")
    .replaceAll(/_+/g, "_")
    .slice(0, 40);

  const pad = (n) => String(n).padStart(2, "0");
  const now = new Date();
  const dateStamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const filename = `Clinical_Assessment_${sanitizedName}_${dateStamp}.pdf`;

  if (globalThis.window !== undefined) {
    doc.save(filename);
  }

  return filename;
};

const extractMultiSelectDefault = (field) => {
  if (Array.isArray(field.value) && field.value.length > 0) {
    return field.value;
  }
  if (Array.isArray(field.options)) {
    const selected = field.options
      .filter((o) => o.is_selected)
      .map((o) => getEffectiveOptionLabel(o));
    return selected.length > 0 ? selected : (field.defaultValue ?? []);
  }
  return field.defaultValue ?? [];
};

const extractSingleSelectDefault = (field) => {
  if (field.value !== undefined && field.value !== null && field.value !== "") {
    return field.value;
  }
  if (Array.isArray(field.options)) {
    const sel = field.options.find((o) => o.is_selected);
    return sel ? getEffectiveOptionLabel(sel) : (field.defaultValue ?? "");
  }
  return field.defaultValue ?? "";
};

const extractBooleanDefault = (field) => {
  if (field.is_checked !== undefined && field.is_checked !== null) {
    return field.is_checked;
  }
  if (field.value !== undefined && field.value !== null && field.value !== "") {
    return field.value;
  }
  return field.defaultValue ?? false;
};

const extractGroupDefault = (field) => {
  const groupVal = {};
  if (field.fields) {
    Object.keys(field.fields).forEach((subKey) => {
      groupVal[subKey] = extractSingleFieldDefault(field.fields[subKey]);
    });
  }
  return groupVal;
};

const extractSingleFieldDefault = (field) => {
  if (!field) return "";
  const fieldType = isCheckboxField(field) ? "checkbox" : field.type || "text";

  if (fieldType === "group") {
    return extractGroupDefault(field);
  }

  let baseVal = "";
  if (fieldType === "multi_select") {
    baseVal = extractMultiSelectDefault(field);
  } else if (fieldType === "single_select") {
    baseVal = extractSingleSelectDefault(field);
  } else if (fieldType === "boolean" || fieldType === "checkbox") {
    baseVal = extractBooleanDefault(field);
  } else {
    baseVal =
      field.value !== undefined && field.value !== null
        ? field.value
        : (field.defaultValue ?? "");
  }

  if (field.branches && typeof field.branches === "object") {
    return { value: baseVal, branches: field.branches };
  }
  return baseVal;
};

const extractAnswersFromSchema = (schema) => {
  const p = {};
  if (schema?.sections) {
    Object.keys(schema.sections).forEach((sectionKey) => {
      p[sectionKey] = {};
      const fields = schema.sections[sectionKey].fields;
      if (fields) {
        Object.keys(fields).forEach((fieldKey) => {
          const field = fields[fieldKey];
          p[sectionKey][fieldKey] = extractSingleFieldDefault(field);
        });
      }
    });
  }
  return p;
};

const resolveFieldValue = (field) => {
  if (field && typeof field === "object" && field.branches) {
    return field;
  }
  const rawValue = field?.value === undefined ? field : field.value;
  return unwrapValue(rawValue);
};

const extractBaseAnswers = (subVal) => {
  const schema = getTemplateSchemaFromPayload({ target: subVal });
  if (schema?.sections) {
    return extractAnswersFromSchema(schema);
  }
  if (subVal.sections && typeof subVal.sections === "object") {
    return extractAnswersFromSchema(subVal);
  }
  return {};
};

const populateSectionFields = (sectionObj, targetSection) => {
  for (const [fK, fV] of Object.entries(sectionObj)) {
    if (
      fK === "fields" ||
      fK === "label" ||
      fK === "title" ||
      fK.startsWith("_")
    ) {
      continue;
    }
    const actualV = resolveFieldValue(fV);
    if (actualV !== undefined && actualV !== null && actualV !== "") {
      targetSection[fK] = actualV;
    }
  }
};

const mergeSubValSections = (subValSections, result) => {
  if (!subValSections || typeof subValSections !== "object") return;
  for (const [secK, secVal] of Object.entries(subValSections)) {
    if (!secVal || typeof secVal !== "object") continue;
    result[secK] = result[secK] || {};
    if (secVal.fields && typeof secVal.fields === "object") {
      populateSectionFields(secVal.fields, result[secK]);
    }
    populateSectionFields(secVal, result[secK]);
  }
};

const getAnswersFromSubPayload = (subVal) => {
  if (!subVal || typeof subVal !== "object") return {};
  const result = { ...extractBaseAnswers(subVal) };

  mergeSubValSections(subVal.sections, result);

  for (const [k, v] of Object.entries(subVal)) {
    if (k === "sections" || k.startsWith("_")) continue;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      result[k] = { ...result[k], ...v };
    } else {
      result[k] = v;
    }
  }

  return result;
};

const isIgnoredPayloadKey = (k) => {
  const upperK = k.toUpperCase();
  return (
    upperK.startsWith("_") ||
    upperK.includes("GENERAL") ||
    upperK.includes("OTHER") ||
    upperK.includes("CORE") ||
    upperK.includes("LIFESTYLE") ||
    upperK.includes("CARE_PLAN") ||
    upperK.includes("CAREPLAN") ||
    upperK.includes("UI_STATE") ||
    upperK.includes("UISTATE") ||
    upperK.includes("OUTCOME") ||
    upperK.includes("METADATA")
  );
};

const resolveCoreSchemaAndPayload = (rawPayload, genKey, res) => {
  let generalSchema = null;
  if (res?.schemaPayload || res?.template?.schemaPayload) {
    generalSchema = parseSchemaPayload(
      res.schemaPayload || res.template.schemaPayload,
    );
  }
  if (genKey) {
    const possible = getTemplateSchemaFromPayload({
      [genKey]: rawPayload[genKey],
    });
    if (possible?.sections && !generalSchema) {
      generalSchema = possible;
    } else if (rawPayload[genKey]?.sections && !generalSchema) {
      generalSchema = rawPayload[genKey];
    }
  }
  const generalFormPayload = genKey
    ? getAnswersFromSubPayload(rawPayload[genKey])
    : {};
  return { generalSchema, generalFormPayload };
};

const resolveLifestyleSchemaAndPayload = (rawPayload, lifestyleKey) => {
  let lifestyleSchema = null;
  if (lifestyleKey) {
    const possible = getTemplateSchemaFromPayload({
      [lifestyleKey]: rawPayload[lifestyleKey],
    });
    if (possible?.sections) {
      lifestyleSchema = possible;
    } else if (rawPayload[lifestyleKey]?.sections) {
      lifestyleSchema = rawPayload[lifestyleKey];
    }
  }
  const lifestyleFormPayload = lifestyleKey
    ? getAnswersFromSubPayload(rawPayload[lifestyleKey])
    : {};
  return { lifestyleSchema, lifestyleFormPayload };
};

const resolveCarePlanSchemaAndPayload = (rawPayload, carePlanKey) => {
  let carePlanSchema = null;
  if (carePlanKey) {
    const possible = getTemplateSchemaFromPayload({
      [carePlanKey]: rawPayload[carePlanKey],
    });
    if (possible?.sections) {
      carePlanSchema = possible;
    } else if (rawPayload[carePlanKey]?.sections) {
      carePlanSchema = rawPayload[carePlanKey];
    }
  }
  const carePlanFormPayload = carePlanKey
    ? getAnswersFromSubPayload(rawPayload[carePlanKey])
    : {};
  return { carePlanSchema, carePlanFormPayload };
};

const processSingleProgramPayloadSchema = (
  k,
  rawPayload,
  programSchemas,
  programFormPayload,
) => {
  if (isIgnoredPayloadKey(k)) return;
  const cleanK = cleanProgramId(k);
  const possible = getTemplateSchemaFromPayload({ [k]: rawPayload[k] });
  let s = null;
  if (possible?.sections) {
    s = possible;
  } else if (rawPayload[k]?.sections) {
    s = rawPayload[k];
  }
  if (s) {
    if (cleanK) {
      programSchemas[cleanK] = s;
      programSchemas[`${cleanK}_PROGRAM_SPECIFIC`] = s;
    }
    programSchemas[k] = s;
  }
  const answers = getAnswersFromSubPayload(rawPayload[k]);
  if (cleanK) {
    programFormPayload[cleanK] = answers;
    programFormPayload[`${cleanK}_PROGRAM_SPECIFIC`] = answers;
  }
  programFormPayload[k] = answers;
};

const mergeProgramAnswers = (programFormPayload, cleanPid, pid, answers) => {
  if (cleanPid) {
    programFormPayload[cleanPid] = {
      ...programFormPayload[cleanPid],
      ...answers,
    };
    programFormPayload[`${cleanPid}_PROGRAM_SPECIFIC`] = {
      ...programFormPayload[`${cleanPid}_PROGRAM_SPECIFIC`],
      ...answers,
    };
  }
  if (pid) {
    programFormPayload[pid] = {
      ...programFormPayload[pid],
      ...answers,
    };
  }
};

const registerProgramSchema = (programSchemas, cleanPid, pid, schema) => {
  if (!schema?.sections) return;
  if (cleanPid) {
    programSchemas[cleanPid] = schema;
    programSchemas[`${cleanPid}_PROGRAM_SPECIFIC`] = schema;
  }
  if (pid) {
    programSchemas[pid] = schema;
  }
};

const processProgramListEntry = (p, programSchemas, programFormPayload) => {
  if (!p || typeof p !== "object") return;
  const pid = p.programId || p.programCode || p.id || p.name;
  const cleanPid = cleanProgramId(pid);
  const subAnswers =
    p.payload || p.answers || p.payloadDelta || p.programPayload;
  if (
    subAnswers &&
    typeof subAnswers === "object" &&
    Object.keys(subAnswers).length > 0
  ) {
    const answers = getAnswersFromSubPayload(subAnswers);
    mergeProgramAnswers(programFormPayload, cleanPid, pid, answers);
  }
  registerProgramSchema(
    programSchemas,
    cleanPid,
    pid,
    p.schema || p.templateSchema,
  );
};

const resolveProgramSchemasAndPayloads = (
  rawPayload,
  payloadKeys,
  res = null,
) => {
  const programSchemas = {};
  const programFormPayload = {};

  payloadKeys.forEach((k) => {
    processSingleProgramPayloadSchema(
      k,
      rawPayload,
      programSchemas,
      programFormPayload,
    );
  });

  const progList = [
    ...(Array.isArray(res?.programs) ? res.programs : []),
    ...(Array.isArray(rawPayload?.programs) ? rawPayload.programs : []),
  ];
  progList.forEach((p) => {
    processProgramListEntry(p, programSchemas, programFormPayload);
  });

  return { programSchemas, programFormPayload };
};

const resolveSchemasAndPayloadsFromDraft = (res, rawPayload) => {
  const payloadKeys = Object.keys(rawPayload);

  const genKey = payloadKeys.find(
    (k) =>
      (k.toUpperCase().includes("GENERAL") ||
        k.toUpperCase().includes("OTHER") ||
        k.toUpperCase().includes("CORE")) &&
      !k.toUpperCase().includes("LIFESTYLE") &&
      !k.toUpperCase().includes("CARE_PLAN") &&
      !k.toUpperCase().includes("METADATA"),
  );

  const lifestyleKey = payloadKeys.find(
    (k) =>
      k.toUpperCase().includes("LIFESTYLE") &&
      !k.toUpperCase().includes("METADATA"),
  );

  const carePlanKey = payloadKeys.find(
    (k) =>
      (k.toUpperCase().includes("CARE_PLAN") ||
        k.toUpperCase().includes("CAREPLAN")) &&
      !k.toUpperCase().includes("METADATA"),
  );

  const { generalSchema, generalFormPayload } = resolveCoreSchemaAndPayload(
    rawPayload,
    genKey,
    res,
  );
  const { lifestyleSchema, lifestyleFormPayload } =
    resolveLifestyleSchemaAndPayload(rawPayload, lifestyleKey);
  const { carePlanSchema, carePlanFormPayload } =
    resolveCarePlanSchemaAndPayload(rawPayload, carePlanKey);
  const { programSchemas, programFormPayload } =
    resolveProgramSchemasAndPayloads(rawPayload, payloadKeys, res);

  return {
    generalSchema,
    lifestyleSchema,
    carePlanSchema,
    programSchemas,
    generalFormPayload,
    lifestyleFormPayload,
    carePlanFormPayload,
    programFormPayload,
  };
};

const parseTemplateFromGroup = async (g, targetType) => {
  if (g.templates && Array.isArray(g.templates)) {
    const tmpl =
      g.templates.find((t) => t.formType === targetType) || g.templates[0];
    if (tmpl?.schemaPayload) {
      return parseSchemaPayload(tmpl.schemaPayload);
    }
  }
  if (g.id) {
    try {
      const fullGroup = await fetchAssessmentGroupById(g.id);
      const tmpl =
        fullGroup?.templates?.find((t) => t.formType === targetType) ||
        fullGroup?.templates?.[0];
      if (tmpl?.schemaPayload) {
        return parseSchemaPayload(tmpl.schemaPayload);
      }
    } catch (e) {
      console.debug("Failed to fetch full assessment group:", e);
    }
  }
  return null;
};

const assignProgramSpecificSchemas = (
  targetSchemas,
  parsed,
  pid,
  cleanPid,
  groupName,
  formType,
) => {
  targetSchemas.programSchemas = targetSchemas.programSchemas || {};
  const clean = pid.replaceAll(/_PROGRAM_SPECIFIC|_PROGRAM/gi, "").trim();
  const keysToAssign = [];
  if (clean) {
    keysToAssign.push(clean, `${clean}_PROGRAM_SPECIFIC`);
    if (formType) {
      keysToAssign.push(
        `${clean}_${formType}`,
        `${clean}_PROGRAM_SPECIFIC_${formType}`,
      );
    }
  }
  if (cleanPid) {
    keysToAssign.push(cleanPid, `${cleanPid}_PROGRAM_SPECIFIC`);
    if (formType) {
      keysToAssign.push(
        `${cleanPid}_${formType}`,
        `${cleanPid}_PROGRAM_SPECIFIC_${formType}`,
      );
    }
  }
  keysToAssign.push(pid);
  if (formType) {
    keysToAssign.push(`${pid}_${formType}`);
  }
  if (groupName) {
    const cleanGName = String(groupName)
      .toUpperCase()
      .replaceAll(/[^A-Z0-9_]/g, "_");
    keysToAssign.push(cleanGName, `${cleanGName}_PROGRAM_SPECIFIC`);
    if (formType) {
      keysToAssign.push(
        `${cleanGName}_${formType}`,
        `${cleanGName}_PROGRAM_SPECIFIC_${formType}`,
      );
    }
  }
  keysToAssign.forEach((k) => {
    targetSchemas.programSchemas[k] = parsed;
  });
};

const assignGroupSchemaByType = (targetSchemas, parsed, g, formType) => {
  const type = String(g.groupType || "").toUpperCase();
  const pid = String(g.programId || "").toUpperCase();
  const cleanPid = cleanProgramId(pid);
  const name = String(g.groupName || "").toLowerCase();

  const isLifestyle =
    type === "LIFESTYLE" ||
    pid.includes("LIFESTYLE") ||
    name.includes("lifestyle");
  const isCarePlan =
    type === "CARE_PLAN" ||
    pid.includes("CARE_PLAN") ||
    name.includes("care plan");
  const isGeneral =
    type === "GENERAL" ||
    pid === "GLOBAL" ||
    pid === "OTHER" ||
    name.includes("general");

  if (isLifestyle) {
    if (!targetSchemas.lifestyleSchema) targetSchemas.lifestyleSchema = parsed;
  } else if (isCarePlan) {
    if (!targetSchemas.carePlanSchema) targetSchemas.carePlanSchema = parsed;
  } else if (isGeneral) {
    if (!targetSchemas.generalSchema) targetSchemas.generalSchema = parsed;
  } else {
    assignProgramSpecificSchemas(
      targetSchemas,
      parsed,
      pid,
      cleanPid,
      g.groupName,
      formType,
    );
  }
};

const groupHasTemplates = (g) => g.templates && Array.isArray(g.templates);

const assignEmbeddedTemplateSchemas = (targetSchemas, g) => {
  for (const tmpl of g.templates) {
    if (tmpl?.schemaPayload) {
      const parsed = parseSchemaPayload(tmpl.schemaPayload);
      if (parsed?.sections) {
        assignGroupSchemaByType(targetSchemas, parsed, g, tmpl.formType);
      }
    }
  }
};

const loadMissingSchemasFromAssessmentGroups = async ({
  allGroups,
  targetType,
  currentSchemas,
}) => {
  const targetSchemas = {
    ...currentSchemas,
    programSchemas: { ...currentSchemas.programSchemas },
  };

  const fetchedTemplates = await Promise.all(
    allGroups.map((g) =>
      groupHasTemplates(g) ? null : parseTemplateFromGroup(g, targetType),
    ),
  );

  allGroups.forEach((g, index) => {
    if (groupHasTemplates(g)) {
      assignEmbeddedTemplateSchemas(targetSchemas, g);
    } else {
      const parsed = fetchedTemplates[index];
      if (parsed?.sections) {
        assignGroupSchemaByType(targetSchemas, parsed, g);
      }
    }
  });

  return targetSchemas;
};

const updateSingleProgramInMap = (programsMap, progData) => {
  const {
    programId,
    name,
    outcome,
    outcomeReason,
    subReason,
    declinedNotes,
    notes,
    manuallyAdded,
    isManual,
    assessmentType: progAssessmentType,
    formType: progFormType,
  } = progData;

  if (!programId && !name) return;
  const rawId = programId || name;
  const cleanId =
    cleanProgramId(rawId) ||
    String(rawId)
      .toUpperCase()
      .replaceAll(/_PROGRAM_SPECIFIC|_PROGRAM/gi, "")
      .trim();
  if (!cleanId) return;

  const existing = programsMap.get(cleanId) || {};
  const finalName =
    name || existing.name || formatProgramName(rawId) || cleanId;
  const finalOutcome = outcome || existing.outcome || "";
  const finalReason = outcomeReason || existing.outcomeReason || null;
  const finalSubReason = subReason || existing.subReason || null;
  const finalNotes =
    declinedNotes || notes || existing.declinedNotes || existing.notes || null;

  let finalManual = Boolean(existing.manuallyAdded || existing.isManual);
  if (manuallyAdded !== undefined) {
    finalManual = Boolean(manuallyAdded);
  } else if (isManual !== undefined) {
    finalManual = Boolean(isManual);
  }

  const finalType =
    progAssessmentType ||
    progFormType ||
    existing.assessmentType ||
    existing.formType ||
    null;

  const finalColor = getRawColor(progData) || getRawColor(existing) || null;

  programsMap.set(cleanId, {
    programId: cleanId,
    name: finalName,
    outcome: finalOutcome,
    outcomeReason: finalReason,
    subReason: finalSubReason,
    notes: finalNotes,
    declinedNotes: finalNotes,
    declinedNote: finalNotes,
    manuallyAdded: finalManual,
    isManual: finalManual,
    assessmentType: finalType,
    formType: finalType,
    colourCode: finalColor,
    outcomeColour: finalColor,
  });
};

const populateProgramsFromBatchItems = (programsMap, allBatchItems) => {
  (allBatchItems || []).forEach((b) => {
    if (!b) return;
    const pId =
      b.programId ||
      b.programCode ||
      (Array.isArray(b.programs)
        ? b.programs[0]?.programId || b.programs[0]
        : b.program);
    const pName =
      b.programName ||
      b.program ||
      (Array.isArray(b.programs)
        ? b.programs[0]?.programName || b.programs[0]?.name
        : null) ||
      pId;
    const pColor =
      getRawColor(b) ||
      (Array.isArray(b.programs) ? getRawColor(b.programs[0]) : null);

    updateSingleProgramInMap(programsMap, {
      programId: pId,
      name: pName,
      outcome:
        b.outcome ||
        b.programOutcome ||
        (Array.isArray(b.programs) ? b.programs[0]?.outcome : null),
      outcomeReason:
        b.outcomeReason ||
        b.reason ||
        b.declinedReason ||
        (Array.isArray(b.programs)
          ? b.programs[0]?.outcomeReason || b.programs[0]?.reason
          : null),
      subReason:
        b.subReason ||
        (Array.isArray(b.programs) ? b.programs[0]?.subReason : null),
      declinedNotes:
        b.declinedNotes ||
        b.declinedNote ||
        b.notes ||
        b.note ||
        (Array.isArray(b.programs)
          ? b.programs[0]?.declinedNotes || b.programs[0]?.notes
          : null),
      manuallyAdded: Boolean(
        b.manuallyAdded ||
        b.isManual ||
        (Array.isArray(b.programs)
          ? b.programs[0]?.manuallyAdded || b.programs[0]?.isManual
          : false),
      ),
      assessmentType:
        b.assessmentType ||
        b.formType ||
        b.type ||
        (Array.isArray(b.programs)
          ? b.programs[0]?.assessmentType ||
            b.programs[0]?.formType ||
            b.programs[0]?.type
          : null),
      colourCode: pColor,
      outcomeColour: pColor,
    });

    if (Array.isArray(b.programs)) {
      b.programs.forEach((prog) => {
        if (!prog) return;
        updateSingleProgramInMap(programsMap, {
          programId:
            typeof prog === "object"
              ? prog.programId || prog.id || prog.programCode || prog.name
              : prog,
          name:
            typeof prog === "object"
              ? prog.programName || prog.name || prog.displayName
              : prog,
          outcome:
            typeof prog === "object"
              ? prog.outcome || prog.programOutcome || prog.outcomeName
              : null,
          outcomeReason:
            typeof prog === "object"
              ? prog.outcomeReason || prog.reason || prog.declinedReason
              : null,
          subReason: typeof prog === "object" ? prog.subReason : null,
          declinedNotes:
            typeof prog === "object"
              ? prog.declinedNotes || prog.declinedNote || prog.notes
              : null,
          manuallyAdded:
            typeof prog === "object"
              ? Boolean(prog.manuallyAdded || prog.isManual)
              : false,
          assessmentType:
            typeof prog === "object"
              ? prog.assessmentType || prog.formType || prog.type
              : null,
          colourCode: getRawColor(prog) || pColor,
          outcomeColour: getRawColor(prog) || pColor,
        });
      });
    }
  });
};

const populateProgramsFromResponseArrays = (
  programsMap,
  res,
  rawPayload,
  mergedItem,
) => {
  const resPrograms = [
    ...(Array.isArray(res?.programs) ? res.programs : []),
    ...(Array.isArray(res?.programOutcomes) ? res.programOutcomes : []),
    ...(Array.isArray(rawPayload?.programs) ? rawPayload.programs : []),
    ...(Array.isArray(rawPayload?.programOutcomes)
      ? rawPayload.programOutcomes
      : []),
    ...(Array.isArray(mergedItem?.programs) ? mergedItem.programs : []),
  ];

  resPrograms.forEach((prog) => {
    if (!prog) return;
    updateSingleProgramInMap(programsMap, {
      programId:
        typeof prog === "object"
          ? prog.programId || prog.id || prog.programCode || prog.name
          : prog,
      name:
        typeof prog === "object"
          ? prog.programName || prog.name || prog.displayName
          : prog,
      outcome:
        typeof prog === "object"
          ? prog.outcome || prog.programOutcome || prog.outcomeName
          : null,
      outcomeReason:
        typeof prog === "object"
          ? prog.outcomeReason || prog.reason || prog.declinedReason
          : null,
      subReason: typeof prog === "object" ? prog.subReason : null,
      declinedNotes:
        typeof prog === "object"
          ? prog.declinedNotes || prog.declinedNote || prog.notes
          : null,
      manuallyAdded:
        typeof prog === "object"
          ? Boolean(prog.manuallyAdded || prog.isManual)
          : false,
      assessmentType:
        typeof prog === "object"
          ? prog.assessmentType || prog.formType || prog.type
          : null,
      colourCode: getRawColor(prog),
      outcomeColour: getRawColor(prog),
    });
  });
};

const findFirstKeyMatch = (keys, map) => {
  const found = keys.find(
    (k) => map?.[k] !== undefined && map?.[k] !== null && map?.[k] !== "",
  );
  return found ? map[found] : null;
};

const resolveDraftOutcomeValues = (candidateKeys, maps) => {
  const noteVal = findFirstKeyMatch(candidateKeys, maps.declinedNotesMap);
  const reasonVal = findFirstKeyMatch(candidateKeys, maps.reasonsMap);
  const subReasonVal = findFirstKeyMatch(candidateKeys, maps.subReasonsMap);
  const manualFound = candidateKeys.find(
    (k) => maps.manualMap?.[k] !== undefined,
  );
  const manualVal = manualFound ? Boolean(maps.manualMap[manualFound]) : false;
  const typeVal = findFirstKeyMatch(candidateKeys, maps.programTypeMap);

  return { noteVal, reasonVal, subReasonVal, manualVal, typeVal };
};

const populateSingleDraftOutcome = (progKey, outcomeVal, maps, programsMap) => {
  if (!outcomeVal) return;
  const cleanKey = cleanProgramId(progKey);
  const candidateKeys = [
    progKey,
    cleanKey,
    String(progKey).toUpperCase(),
    String(cleanKey).toUpperCase(),
  ];

  const { noteVal, reasonVal, subReasonVal, manualVal, typeVal } =
    resolveDraftOutcomeValues(candidateKeys, maps);

  updateSingleProgramInMap(programsMap, {
    programId: cleanKey,
    name: formatProgramName(progKey),
    outcome: outcomeVal,
    outcomeReason: reasonVal,
    subReason: subReasonVal,
    declinedNotes: noteVal,
    notes: noteVal,
    manuallyAdded: manualVal,
    isManual: manualVal,
    assessmentType: typeVal,
    formType: typeVal,
  });
};

const populateProgramsFromDraftOutcomes = (programsMap, rawPayload) => {
  const draftOutcomes = rawPayload?._outcomes || rawPayload?._outcome || {};
  const selectedOutcomesMap = draftOutcomes.selectedOutcome || {};
  const maps = {
    subReasonsMap: draftOutcomes.selectedSubReason || {},
    reasonsMap: draftOutcomes.declinedReason || {},
    declinedNotesMap:
      draftOutcomes.declinedNotes || draftOutcomes.declinedNote || {},
    manualMap:
      draftOutcomes.manuallyAdded ||
      draftOutcomes.isManual ||
      rawPayload?.manuallyAdded ||
      {},
    programTypeMap:
      draftOutcomes.programAssessmentTypes ||
      draftOutcomes.formTypes ||
      rawPayload?.programAssessmentTypes ||
      {},
  };

  Object.keys(selectedOutcomesMap).forEach((progKey) => {
    populateSingleDraftOutcome(
      progKey,
      selectedOutcomesMap[progKey],
      maps,
      programsMap,
    );
  });
};

const populateProgramsFromAllSources = ({
  allBatchItems,
  res,
  rawPayload,
  mergedItem,
}) => {
  const programsMap = new Map();
  populateProgramsFromBatchItems(programsMap, allBatchItems);
  populateProgramsFromResponseArrays(programsMap, res, rawPayload, mergedItem);
  populateProgramsFromDraftOutcomes(programsMap, rawPayload);
  return programsMap;
};

const resolveRootValueFromCandidates = (sourceObj, candidateKeys) => {
  if (!sourceObj) return null;
  if (typeof sourceObj === "string") return sourceObj;
  if (typeof sourceObj === "object") {
    for (const k of candidateKeys) {
      if (sourceObj[k]) return sourceObj[k];
    }
  }
  return null;
};

const enrichSingleProgramWithNotesAndReasons = (
  p,
  rootDeclinedNotes,
  rootReasons,
  rootSubReasons,
) => {
  const candidateKeys = [
    p.programId,
    cleanProgramId(p.programId),
    String(p.programId).toUpperCase(),
    p.name,
    String(p.name).toUpperCase(),
  ].filter(Boolean);

  const noteFromRoot = resolveRootValueFromCandidates(
    rootDeclinedNotes,
    candidateKeys,
  );
  const reasonFromRoot = resolveRootValueFromCandidates(
    rootReasons,
    candidateKeys,
  );
  const subReasonFromRoot = resolveRootValueFromCandidates(
    rootSubReasons,
    candidateKeys,
  );

  const finalNotes =
    p.declinedNotes || p.declinedNote || p.notes || noteFromRoot || null;
  const finalReason = p.outcomeReason || p.reason || reasonFromRoot || null;
  const finalSubReason = p.subReason || subReasonFromRoot || null;

  return {
    ...p,
    outcomeReason: finalReason,
    subReason: finalSubReason,
    notes: finalNotes,
    declinedNotes: finalNotes,
    declinedNote: finalNotes,
  };
};

const enrichProgramsWithRootNotesAndReasons = (
  programsMap,
  res,
  rawPayload,
  mergedItem,
) => {
  const rootDeclinedNotes =
    res?.declinedNotes ||
    res?.declinedNote ||
    rawPayload?.declinedNotes ||
    rawPayload?.declinedNote ||
    rawPayload?._outcomes?.declinedNotes ||
    rawPayload?._outcomes?.declinedNote ||
    mergedItem?.declinedNotes ||
    mergedItem?.declinedNote ||
    {};

  const rootReasons =
    rawPayload?._outcomes?.declinedReason ||
    rawPayload?.declinedReason ||
    res?.declinedReason ||
    mergedItem?.declinedReason ||
    {};

  const rootSubReasons =
    rawPayload?._outcomes?.selectedSubReason ||
    rawPayload?.selectedSubReason ||
    res?.selectedSubReason ||
    mergedItem?.selectedSubReason ||
    {};

  return Array.from(programsMap.values()).map((p) =>
    enrichSingleProgramWithNotesAndReasons(
      p,
      rootDeclinedNotes,
      rootReasons,
      rootSubReasons,
    ),
  );
};

const resolveHistoryAssessmentMeta = (mergedItem, item, res, rawPayload) => {
  const resolvedAssigneeRn =
    mergedItem.assigneeRnName ||
    mergedItem.assignee_rn_name ||
    item.assigneeRnName ||
    item.assignee_rn_name ||
    res?.assigneeRnName ||
    res?.assignee_rn_name ||
    res?.member?.assigneeRnName ||
    rawPayload?.assigneeRnName ||
    rawPayload?.assignee_rn_name ||
    rawPayload?._outcomes?.assigneeRnName ||
    "";

  const resolvedSubmittedBy =
    resolvedAssigneeRn ||
    mergedItem.submittedBy ||
    mergedItem.submittedByName ||
    mergedItem.clinicianSignature ||
    mergedItem.clinician_signature ||
    rawPayload?.narrative_attestation?.clinician_signature ||
    rawPayload?.signature?.signeeName ||
    rawPayload?._outcomes?.submittedBy ||
    item.submittedBy ||
    item.submittedByName ||
    "";

  const resolvedClinicalNotes =
    mergedItem.clinicalNote ||
    mergedItem.clinicalNotes ||
    mergedItem.narrative ||
    mergedItem.note ||
    mergedItem.notes ||
    mergedItem.summary ||
    mergedItem.comments ||
    rawPayload?.narrative_attestation?.narrative_note ||
    rawPayload?.narrative ||
    rawPayload?.clinicalNote ||
    rawPayload?.clinicalNotes ||
    rawPayload?._outcomes?.clinicalNotes ||
    rawPayload?._outcomes?.clinicalNote ||
    rawPayload?._outcomes?.narrative ||
    rawPayload?._outcome?.clinicalNotes ||
    rawPayload?._outcome?.clinicalNote ||
    rawPayload?._outcome?.narrative ||
    item.clinicalNote ||
    item.clinicalNotes ||
    item.narrative ||
    "";

  const resolvedSignatureDate =
    mergedItem.signatureDate ||
    rawPayload?.narrative_attestation?.signature_date ||
    mergedItem.completionTime ||
    item.completionTime ||
    mergedItem.startTime ||
    item.startTime ||
    null;

  let resolvedSignatureConfirmed = true;
  if (mergedItem.signatureConfirmed !== undefined) {
    resolvedSignatureConfirmed = mergedItem.signatureConfirmed;
  } else if (res?.signatureConfirmed !== undefined) {
    resolvedSignatureConfirmed = res.signatureConfirmed;
  }

  return {
    resolvedSubmittedBy,
    resolvedAssigneeRn,
    resolvedClinicalNotes,
    resolvedSignatureDate,
    resolvedSignatureConfirmed,
  };
};

const fetchHistoryAssessmentGroup = async (gid) => {
  try {
    return await fetchAssessmentGroupById(gid);
  } catch (e) {
    console.debug("Failed to fetch assessment group schema:", e);
    return null;
  }
};

const processHistoryAssessmentGroup = (
  groupData,
  targetType,
  targetSchemas,
) => {
  try {
    if (groupData?.templates && Array.isArray(groupData.templates)) {
      assignEmbeddedTemplateSchemas(targetSchemas, groupData);
    } else {
      const tmpl = groupData?.templates?.find((t) => t.formType === targetType);
      if (tmpl?.schemaPayload) {
        const parsed = parseSchemaPayload(tmpl.schemaPayload);
        if (parsed?.sections) {
          assignGroupSchemaByType(targetSchemas, parsed, groupData);
        }
      }
    }
  } catch (e) {
    console.debug("Failed to fetch assessment group schema:", e);
  }
};

const enrichHistorySchemasFromGroups = async ({
  res,
  targetType,
  generalSchema,
  lifestyleSchema,
  carePlanSchema,
  programSchemas,
}) => {
  const targetSchemas = {
    generalSchema,
    lifestyleSchema,
    carePlanSchema,
    programSchemas: { ...programSchemas },
  };

  if (res?.assessmentGroupIds && Array.isArray(res.assessmentGroupIds)) {
    const groups = await Promise.all(
      res.assessmentGroupIds.map(fetchHistoryAssessmentGroup),
    );
    groups.forEach((groupData) => {
      processHistoryAssessmentGroup(groupData, targetType, targetSchemas);
    });
  }

  return targetSchemas;
};

const fetchHistoryDraftSafe = async (targetId, item) => {
  try {
    const params = { isBatch: Boolean(item.batchId || item.isBatch) };
    return await fetchDraftPayloadApi(targetId, params);
  } catch (err) {
    console.error("Failed to fetch assessment draft for PDF:", err);
    return null;
  }
};

const fetchFallbackGeneralSchema = async (generalSchema, res, item) => {
  if (generalSchema) return generalSchema;
  try {
    const fallbackTmpl = await fetchAssessmentTemplateByType(
      res?.assessmentType || item.assessmentType || "INITIAL",
    );
    if (
      fallbackTmpl?.sections &&
      Object.keys(fallbackTmpl.sections).length > 0
    ) {
      return fallbackTmpl;
    }
  } catch (e) {
    console.debug("Failed to fetch fallback template by type:", e);
  }
  return null;
};

const buildEffectiveMemberData = (mergedItem, res, memberData) => {
  const effectiveMemberData = {
    carrierName:
      mergedItem?.carrierName ||
      res?.carrierName ||
      mergedItem?.carrier ||
      res?.carrier,
    carrierCode:
      mergedItem?.carrierCode ||
      res?.carrierCode ||
      mergedItem?.carrierId ||
      res?.carrierId,
    groupName:
      mergedItem?.groupName ||
      res?.groupName ||
      mergedItem?.group ||
      res?.group,
    groupNum:
      mergedItem?.groupNum ||
      res?.groupNum ||
      mergedItem?.groupId ||
      res?.groupId,
    subscriberNum:
      mergedItem?.subscriberNum ||
      res?.subscriberNum ||
      mergedItem?.member?.subscriberNum ||
      res?.member?.subscriberNum ||
      memberData?.subscriberNum ||
      "",
    dob: mergedItem?.dob || res?.dob,
    memberId:
      mergedItem?.memberId ||
      mergedItem?.member_id ||
      res?.memberId ||
      res?.member_id,
    ...mergedItem?.member,
    ...res?.member,
    ...res?.demographics,
    ...memberData,
  };

  if (!effectiveMemberData.assigneeRnName) {
    effectiveMemberData.assigneeRnName =
      mergedItem.assigneeRnName ||
      mergedItem.assignee_rn_name ||
      res?.assigneeRnName ||
      res?.assignee_rn_name ||
      res?.member?.assigneeRnName ||
      memberData?.assigneeRnName ||
      memberData?.assignee_rn_name ||
      memberData?.member?.assigneeRnName ||
      "";
  }

  if (
    !effectiveMemberData.memberName &&
    (effectiveMemberData.firstName || effectiveMemberData.lastName)
  ) {
    effectiveMemberData.memberName = `${effectiveMemberData.firstName || ""} ${
      effectiveMemberData.lastName || ""
    }`.trim();
  }

  return effectiveMemberData;
};

const loadAllHistoricalSchemas = async (res, rawPayload, item) => {
  const draftSchemas = resolveSchemasAndPayloadsFromDraft(res, rawPayload);
  let { generalSchema, lifestyleSchema, carePlanSchema, programSchemas } =
    draftSchemas;
  programSchemas = { ...programSchemas };

  const targetType = (
    res?.assessmentType ||
    item.assessmentType ||
    "INITIAL"
  ).toUpperCase();

  try {
    const allGroups = await fetchAssessmentGroups();
    if (Array.isArray(allGroups)) {
      const updatedSchemas = await loadMissingSchemasFromAssessmentGroups({
        allGroups,
        targetType,
        currentSchemas: {
          generalSchema,
          lifestyleSchema,
          carePlanSchema,
          programSchemas,
        },
      });
      generalSchema = updatedSchemas.generalSchema;
      lifestyleSchema = updatedSchemas.lifestyleSchema;
      carePlanSchema = updatedSchemas.carePlanSchema;
      programSchemas = {
        ...programSchemas,
        ...updatedSchemas.programSchemas,
      };
    }
  } catch (err) {
    console.debug("Failed to fetch assessment groups for PDF:", err);
  }

  const enrichedSchemas = await enrichHistorySchemasFromGroups({
    res,
    targetType,
    generalSchema,
    lifestyleSchema,
    carePlanSchema,
    programSchemas,
  });

  generalSchema = await fetchFallbackGeneralSchema(
    enrichedSchemas.generalSchema,
    res,
    item,
  );

  return {
    ...draftSchemas,
    generalSchema,
    lifestyleSchema: enrichedSchemas.lifestyleSchema,
    carePlanSchema: enrichedSchemas.carePlanSchema,
    programSchemas: {
      ...programSchemas,
      ...enrichedSchemas.programSchemas,
    },
  };
};

const hasUsableValue = (value) => !!value && value !== "N/A";

const enrichMemberDataFromOverview = async (
  effectiveMemberData,
  targetMemberId,
) => {
  const hasName = hasUsableValue(effectiveMemberData.name);
  const hasDob = hasUsableValue(effectiveMemberData.dob);
  const hasSubscriberNum = hasUsableValue(effectiveMemberData.subscriberNum);

  if ((!hasName || !hasDob || !hasSubscriberNum) && targetMemberId) {
    try {
      const fetchedMember = await fetchMemberOverview(targetMemberId);
      if (fetchedMember) {
        return {
          ...fetchedMember,
          ...effectiveMemberData,
          name: hasName ? effectiveMemberData.name : fetchedMember.name,
          dob: hasDob ? effectiveMemberData.dob : fetchedMember.dob,
          carrierName:
            effectiveMemberData.carrierName || fetchedMember.carrierName,
          carrierCode:
            effectiveMemberData.carrierCode || fetchedMember.carrierCode,
          groupName: effectiveMemberData.groupName || fetchedMember.groupName,
          groupNum: effectiveMemberData.groupNum || fetchedMember.groupNum,
          subscriberNum: hasSubscriberNum
            ? effectiveMemberData.subscriberNum
            : fetchedMember.subscriberNum || effectiveMemberData.subscriberNum,
        };
      }
    } catch (e) {
      console.debug("Failed to fetch member overview for PDF demographics:", e);
    }
  }

  return effectiveMemberData;
};

/**
 * Downloads a clinical assessment PDF for a historical assessment record.
 */
export const exportClinicalAssessmentPdfFromHistory = async ({
  item = {},
  allBatchItems = [],
  memberData = {},
}) => {
  const targetId = item.batchId || item.id || item.clinicalAssessmentId;
  if (!targetId) {
    throw new Error("No valid clinical assessment ID provided for PDF export.");
  }

  const res = (await fetchHistoryDraftSafe(targetId, item)) || {};
  const actualRes =
    res?.data && typeof res.data === "object" && !res.payload ? res.data : res;
  const mergedItem = { ...item, ...actualRes };
  const rawPayload =
    actualRes?.payload ||
    actualRes?.finalPayload ||
    actualRes?.payloadDelta ||
    item?.payload ||
    {};

  const {
    generalSchema,
    lifestyleSchema,
    carePlanSchema,
    programSchemas,
    generalFormPayload,
    lifestyleFormPayload,
    carePlanFormPayload,
    programFormPayload,
  } = await loadAllHistoricalSchemas(actualRes, rawPayload, item);

  const programsMap = populateProgramsFromAllSources({
    allBatchItems,
    res,
    rawPayload,
    mergedItem,
  });

  const enrichedProgramsList = enrichProgramsWithRootNotesAndReasons(
    programsMap,
    res,
    rawPayload,
    mergedItem,
  );

  const activePrograms = enrichedProgramsList.map((p) => ({
    id: p.programId,
    name: p.name,
    outcome: p.outcome,
    manuallyAdded: Boolean(p.manuallyAdded || p.isManual),
    isManual: Boolean(p.manuallyAdded || p.isManual),
    assessmentType: p.assessmentType || p.formType,
    formType: p.formType || p.assessmentType,
  }));

  const {
    resolvedSubmittedBy,
    resolvedAssigneeRn,
    resolvedClinicalNotes,
    resolvedSignatureDate,
    resolvedSignatureConfirmed,
  } = resolveHistoryAssessmentMeta(mergedItem, item, res, rawPayload);

  let effectiveMemberData = buildEffectiveMemberData(
    mergedItem,
    res,
    memberData,
  );

  const targetMemberId =
    effectiveMemberData.memberId ||
    effectiveMemberData.id ||
    mergedItem.memberId ||
    mergedItem.member_id ||
    mergedItem.id ||
    res?.memberId ||
    res?.member_id ||
    item.memberId ||
    item.member_id ||
    item.id ||
    memberData?.memberId ||
    memberData?.id ||
    effectiveMemberData.subscriberNum;

  effectiveMemberData = await enrichMemberDataFromOverview(
    effectiveMemberData,
    targetMemberId,
  );

  let referenceData =
    item.referenceData ||
    res?.referenceData ||
    getCachedAssessmentReferenceData?.() ||
    null;

  if (!referenceData) {
    try {
      referenceData = await fetchAssessmentReferenceData().catch(() => null);
    } catch (e) {
      console.debug("Failed to fetch assessment reference data for PDF:", e);
    }
  }

  const reportData = compileAssessmentReportData({
    memberData: effectiveMemberData,
    assessmentType:
      mergedItem.assessmentType || item.assessmentType || "INITIAL",
    status: mergedItem.status || item.status || res?.status || null,
    completionTime: mergedItem.completionTime || item.completionTime || null,
    startTime: mergedItem.startTime || item.startTime || null,
    duration: mergedItem.duration || item.duration || null,
    assigneeRnName: resolvedAssigneeRn || "",
    submittedBy: resolvedSubmittedBy,
    signeeName: resolvedAssigneeRn || resolvedSubmittedBy,
    signatureDate: resolvedSignatureDate,
    signatureConfirmed: resolvedSignatureConfirmed,
    programs: enrichedProgramsList,
    clinicalNotes: resolvedClinicalNotes,
    generalSchema,
    generalPayload: generalFormPayload,
    generalTitle: "Core Assessment",
    programSchemas,
    programPayloads: programFormPayload,
    activePrograms,
    lifestyleSchema,
    lifestylePayload: lifestyleFormPayload,
    lifestyleTitle: "Lifestyle, Barriers and Functional Assessment",
    carePlanSchema,
    carePlanPayload: carePlanFormPayload,
    carePlanTitle: "Care Plan, Coordination and Completion",
    referenceData,
  });

  return generateClinicalAssessmentPdf(reportData);
};
